import type { SupportedLocale } from './locale'

type TranslationKeys = {
  // Navigation
  'nav.dashboard': string
  'nav.evaluations': string
  'nav.account': string
  'nav.settings': string
  'nav.logout': string
  'nav.workspace': string
  'nav.manageAccount': string
  'nav.manageWorkspace': string
  'nav.playWalkthrough': string
  'nav.documentation': string
  'nav.support': string

  // Account page
  'account.title': string
  'account.myProfile': string
  'account.preferences': string
  'account.security': string
  'account.language': string
  'account.email': string
  'account.password': string
  'account.loginConnections': string
  'account.firstName': string
  'account.lastName': string
  'account.changePassword': string
  'account.currentPassword': string
  'account.newPassword': string
  'account.confirmPassword': string

  // Common actions
  'action.save': string
  'action.cancel': string
  'action.delete': string
  'action.edit': string
  'action.create': string
  'action.close': string
  'action.confirm': string
  'action.search': string
  'action.filter': string
  'action.export': string
  'action.import': string
  'action.refresh': string
  'action.back': string
  'action.next': string
  'action.previous': string
  'action.submit': string
  'action.reset': string
  'action.update': string
  'action.add': string
  'action.remove': string
  'action.copy': string
  'action.download': string
  'action.share': string
  'action.invite': string
  'action.retry': string
  'action.reload': string
  'action.saving': string
  'action.ok': string

  // Dashboard
  'dashboard.title': string
  'dashboard.recentEvaluations': string
  'dashboard.noEvaluations': string
  'dashboard.createEvaluation': string
  'dashboard.lastUpdated': string
  'dashboard.status': string
  'dashboard.viewAll': string
  'dashboard.gettingStarted': string
  'dashboard.myEvaluations': string
  'dashboard.shared': string
  'dashboard.archived': string
  'dashboard.backToAccount': string
  'dashboard.backToDashboard': string
  'dashboard.emptyOwned': string
  'dashboard.emptyShared': string
  'dashboard.emptyArchived': string
  'dashboard.createFirst': string
  'dashboard.watchWalkthrough': string
  'dashboard.watchVideo': string
  'dashboard.createFirstEval': string
  'dashboard.scheduleCall': string
  'dashboard.bookExpert': string

  // Evaluation
  'eval.title': string
  'eval.newEvaluation': string
  'eval.products': string
  'eval.scores': string
  'eval.introduction': string
  'eval.conclusion': string
  'eval.changeLogs': string
  'eval.template': string
  'eval.status.draft': string
  'eval.status.active': string
  'eval.status.archived': string
  'eval.status.completed': string
  'eval.recover': string
  'eval.updating': string
  'eval.loading': string

  // Common labels
  'label.name': string
  'label.description': string
  'label.date': string
  'label.type': string
  'label.category': string
  'label.vendor': string
  'label.product': string
  'label.score': string
  'label.rating': string
  'label.notes': string
  'label.comments': string
  'label.createdAt': string
  'label.updatedAt': string
  'label.createdBy': string
  'label.all': string
  'label.none': string
  'label.loading': string
  'label.noResults': string
  'label.error': string
  'label.success': string
  'label.warning': string
  'label.lastModified': string
  'label.owner': string
  'label.collaborators': string

  'nav.talkToExpert': string

  // Evaluation creation
  'evalCreate.categoryHeading': string
  'evalCreate.categorySubtext': string
  'evalCreate.categorySearch': string
  'evalCreate.categoryNoProducts': string
  'evalCreate.categoryStale': string
  'evalCreate.categoryRequestRefresh': string
  'evalCreate.categoryProductsIncluded': string
  'evalCreate.categoryNotFound': string
  'evalCreate.useCaseHeading': string
  'evalCreate.useCaseSubtext': string
  'evalCreate.useCaseSearch': string
  'evalCreate.useCaseSelectAll': string
  'evalCreate.useCaseDeselectAll': string
  'evalCreate.useCaseSelected': string
  'evalCreate.useCaseLooksGood': string
  'evalCreate.useCaseNone': string
  'evalCreate.useCaseNotFound': string
  'evalCreate.requestNewCategory': string
  'evalCreate.categoryPanelHeading': string
  'evalCreate.categoryPanelSubtext': string

  // Workspace
  'workspace.manage': string
  'workspace.general': string
  'workspace.members': string
  'workspace.billing': string
  'workspace.billingPlan': string
  'workspace.billingPaymentInfo': string
  'workspace.billingHistory': string

  // Evaluation data enums
  'rating.Great': string
  'rating.Good': string
  'rating.OK': string
  'rating.Poor': string
  'rating.Excellent': string
  'rating.Acceptable': string
  'rating.NA': string
  'priority.Critical': string
  'priority.Important': string
  'priority.NiceToHave': string
  'priority.DontCare': string

  // Single-select placeholder
  'eval.selectAnOption': string

  // Evaluation sections
  'section.Features': string
  'section.Dimensions': string
  'section.Requirements': string
  'section.Products': string
  'section.ImpactByDimension': string
  'section.ImpactByRequirement': string

  // Evaluation UI
  'eval.createNew': string
  'eval.addDimension': string
  'eval.addRequirement': string
  'eval.addFeature': string
  'eval.rank': string
  'eval.rankExplanation': string
  'eval.criticalFeatures': string
  'eval.importantFeatures': string
  'eval.meets': string
  'eval.doesNotMeet': string
  'eval.ratingLabel': string
  'eval.reasoning': string
  'eval.reasoningPlaceholder': string
  'eval.reasoningWarning': string

  // Scoring chart
  'eval.tabReport': string
  'eval.tabTables': string
  'eval.tabQuestions': string
  'eval.share': string

  'eval.rename': string
  'eval.copyLink': string
  'eval.downloadReport': string
  'eval.downloadTables': string
  'eval.downloadScoreDetail': string
  'eval.archive': string
  'eval.unarchive': string
  'eval.versions': string
  'eval.newVersion': string

  'chart.meetsAll': string
  'chart.meetsCriticalNotImportant': string
  'chart.meetsImportantNotCritical': string
  'chart.meetsNone': string
  'chart.noProducts': string

  // Batch mode
  'eval.batchMode': string
  'eval.batchChanges': string
  'eval.batchModeActive': string
  'eval.batchModeExitFirst': string

  // Status indicator
  'eval.disconnected': string
  'eval.repairing': string
  'eval.restoring': string
  'eval.syncing': string
  'eval.updatingCount': string
  'eval.updatesFailed': string
  'eval.updated': string

  // Upgrade sidebar
  'eval.outOfFreeChanges': string
  'eval.upgradeToUnlock': string

  // Multi-select / tag buttons
  'eval.addAnother': string

  // Comparison table
  'comparison.dimension': string
  'comparison.requirement': string
  'comparison.feature': string
  'comparison.weight': string
  'comparison.priority': string
  'comparison.impact': string
  'comparison.selectType': string
  'comparison.selectProduct': string
  'comparison.noRows': string
  'comparison.comparison': string

  // Sidebar
  'sidebar.contents': string
  'sidebar.tables': string
  'sidebar.questions': string
  'sidebar.changeLog': string

  // Batch mode
  'batch.enterTitle': string
  'batch.enterDescription': string
  'batch.enterConfirm': string
  'batch.cancelTitle': string
  'batch.cancelDescription': string
  'batch.cancelConfirm': string

  // Filter
  'filter.filteringBy': string
  'filter.selectAll': string
  'filter.deselectAll': string
  'filter.noContent': string

  // Error messages
  'error.somethingWentWrong': string
  'error.tryAgainOrRefresh': string
  'error.contactSupport': string
  'error.pageNotFound': string
  'error.returnToDashboard': string
  'error.accessDenied': string
  'error.accessDeniedDescription': string

  // Status messages
  'status.viewOnly': string
  'status.linkCopied': string
  'status.noneAvailable': string
  'status.saved': string
  'status.saving': string
  'status.connecting': string
  'status.connected': string

  // Workspace management
  'workspace.parentWorkspace': string
  'workspace.projectCode': string
  'workspace.createNew': string
  'workspace.name': string
  'workspace.editWorkspace': string
  'workspace.deleteWorkspace': string
  'workspace.deleteConfirm': string
  'workspace.inviteMembers': string
  'workspace.role': string
  'workspace.admin': string
  'workspace.member': string
  'workspace.viewer': string
  'workspace.removeMember': string
  'workspace.pendingInvites': string
  'workspace.noMembers': string
  'workspace.workspaceId': string

  // Invite
  'invite.emailPlaceholder': string
  'invite.sendInvite': string
  'invite.inviteSent': string
  'invite.inviteUsers': string
  'invite.revokeInvite': string

  // Toast messages
  'toast.linkCopied': string
  'toast.changesSaved': string
  'toast.evaluationArchived': string
  'toast.evaluationUnarchived': string
  'toast.evaluationDeleted': string
  'toast.evaluationRenamed': string
  'toast.batchModeActive': string
  'toast.switchedToEach': string
  'toast.errorOccurred': string
  'toast.archivingEvaluation': string
  'toast.unarchivingEvaluation': string
  'toast.deletingEvaluation': string
  'toast.creatingVersion': string
  'toast.versionCreated': string

  // Confirmation dialogs
  'confirm.deleteEvalTitle': string
  'confirm.deleteEvalDescription': string
  'confirm.recoverEvalTitle': string
  'confirm.recoverEvalDescription': string

  // Changelog
  'changelog.tooltip': string
  'changelog.batchWarning': string
  'changelog.batchMode': string

  // Editor toolbar
  'editor.pasteLink': string
  'editor.textToDisplay': string
  'editor.visitLink': string
  'editor.editLink': string
  'editor.removeLink': string
  'editor.link': string
  'editor.comment': string
  'editor.bold': string
  'editor.italic': string
  'editor.underline': string
  'editor.editComment': string
  'editor.deleteComment': string

  // Account / Profile
  'account.emailAddress': string
  'account.currentEmail': string
  'account.emailVerify': string
  'account.confirmNewPassword': string
  'account.savePassword': string
  'account.updatingProfile': string
  'account.profileUpdated': string
  'account.profileUpdateError': string
  'account.updatingPassword': string
}

export type TranslationKey = keyof TranslationKeys

const en: TranslationKeys = {
  'nav.dashboard': 'Dashboard',
  'nav.evaluations': 'Evaluations',
  'nav.account': 'Account',
  'nav.settings': 'Settings',
  'nav.logout': 'Log out',
  'nav.workspace': 'Workspace',
  'nav.manageAccount': 'Manage Account',
  'nav.manageWorkspace': 'Manage Workspace',
  'nav.playWalkthrough': 'Play Walkthrough',
  'nav.documentation': 'Documentation',
  'nav.support': 'Support',
  'nav.talkToExpert': 'Talk to an expert',

  'account.title': 'My account',
  'account.myProfile': 'My profile',
  'account.preferences': 'Preferences',
  'account.security': 'Account security',
  'account.language': 'Language',
  'account.email': 'Email',
  'account.password': 'Password',
  'account.loginConnections': 'Login connections',
  'account.firstName': 'First name',
  'account.lastName': 'Last name',
  'account.changePassword': 'Change password',
  'account.currentPassword': 'Current password',
  'account.newPassword': 'New password',
  'account.confirmPassword': 'Confirm password',

  'action.save': 'Save',
  'action.cancel': 'Cancel',
  'action.delete': 'Delete',
  'action.edit': 'Edit',
  'action.create': 'Create',
  'action.close': 'Close',
  'action.confirm': 'Confirm',
  'action.search': 'Search',
  'action.filter': 'Filter',
  'action.export': 'Export',
  'action.import': 'Import',
  'action.refresh': 'Refresh',
  'action.back': 'Back',
  'action.next': 'Next',
  'action.previous': 'Previous',
  'action.submit': 'Submit',
  'action.reset': 'Reset',
  'action.update': 'Update',
  'action.add': 'Add',
  'action.remove': 'Remove',
  'action.copy': 'Copy',
  'action.download': 'Download',
  'action.share': 'Share',
  'action.invite': 'Invite',
  'action.retry': 'Try again',
  'action.reload': 'Reload',
  'action.saving': 'Saving...',
  'action.ok': 'OK',

  'dashboard.title': 'Dashboard',
  'dashboard.recentEvaluations': 'Recent evaluations',
  'dashboard.noEvaluations': 'No evaluations yet',
  'dashboard.createEvaluation': 'Create evaluation',
  'dashboard.lastUpdated': 'Last updated',
  'dashboard.status': 'Status',
  'dashboard.viewAll': 'View all',
  'dashboard.gettingStarted': 'Getting Started',
  'dashboard.myEvaluations': 'My Evaluations',
  'dashboard.shared': 'Shared',
  'dashboard.archived': 'Archived',
  'dashboard.backToAccount': 'Back to account',
  'dashboard.backToDashboard': 'Back to Dashboard',
  'dashboard.emptyOwned': 'Any evaluations you create or receive will appear here.',
  'dashboard.emptyShared': 'Any evaluations you are a collaborator on will appear here.',
  'dashboard.emptyArchived': 'Any archived evaluations will appear here.',
  'dashboard.createFirst': 'Create my first evaluation',
  'dashboard.watchWalkthrough': 'Watch walkthrough',
  'dashboard.watchVideo': 'Watch our getting started video',
  'dashboard.createFirstEval': 'Create your first evaluation',
  'dashboard.scheduleCall': 'Schedule a call',
  'dashboard.bookExpert': 'Book time with one of our experts',

  'eval.title': 'Evaluation',
  'eval.newEvaluation': 'New evaluation',
  'eval.products': 'Products',
  'eval.scores': 'Scores',
  'eval.introduction': 'Introduction',
  'eval.conclusion': 'Conclusion',
  'eval.changeLogs': 'Change logs',
  'eval.template': 'Template',
  'eval.status.draft': 'Draft',
  'eval.status.active': 'Active',
  'eval.status.archived': 'Archived',
  'eval.status.completed': 'Completed',
  'eval.recover': 'Recover',
  'eval.updating': 'Updating...',
  'eval.loading': 'Loading...',

  'label.name': 'Name',
  'label.description': 'Description',
  'label.date': 'Date',
  'label.type': 'Type',
  'label.category': 'Category',
  'label.vendor': 'Vendor',
  'label.product': 'Product',
  'label.score': 'Score',
  'label.rating': 'Rating',
  'label.notes': 'Notes',
  'label.comments': 'Comments',
  'label.createdAt': 'Created',
  'label.updatedAt': 'Updated',
  'label.createdBy': 'Created by',
  'label.all': 'All',
  'label.none': 'None',
  'label.loading': 'Loading...',
  'label.noResults': 'No results found',
  'label.error': 'An error occurred',
  'label.success': 'Success',
  'label.warning': 'Warning',
  'label.lastModified': 'Last Modified',
  'label.owner': 'Owner',
  'label.collaborators': 'Collaborators',

  'evalCreate.categoryHeading': 'Save 20+ hours on research in 5 minutes',
  'evalCreate.categorySubtext': 'Please select a product category from the list below.',
  'evalCreate.categorySearch': 'Search or click on an option',
  'evalCreate.categoryNoProducts': "Couldn't find any products for this category.",
  'evalCreate.categoryStale': 'This category is more than 90 days old.',
  'evalCreate.categoryRequestRefresh': 'Request a refresh',
  'evalCreate.categoryProductsIncluded': 'The following products are included:',
  'evalCreate.categoryNotFound': "Don't see something you need?",
  'evalCreate.useCaseHeading': 'What use cases are relevant to you?',
  'evalCreate.useCaseSubtext': 'We selected some fundamental use cases to get you started, but you can add as many as you like.',
  'evalCreate.useCaseSearch': 'Search use cases',
  'evalCreate.useCaseSelectAll': 'Select all',
  'evalCreate.useCaseDeselectAll': 'De-select all',
  'evalCreate.useCaseSelected': 'Use Cases Selected',
  'evalCreate.useCaseLooksGood': 'Looks good',
  'evalCreate.useCaseNone': 'No use case found.',
  'evalCreate.useCaseNotFound': 'No use cases found for this category.',
  'evalCreate.requestNewCategory': 'Request a new category',
  'evalCreate.categoryPanelHeading': "Don't see something you need?",
  'evalCreate.categoryPanelSubtext': 'We can research a new category for you within 2 business days.',

  'workspace.manage': 'Manage workspace',
  'workspace.general': 'General',
  'workspace.members': 'Members',
  'workspace.billing': 'Billing',
  'workspace.billingPlan': 'Plan',
  'workspace.billingPaymentInfo': 'Payment information',
  'workspace.billingHistory': 'Billing history',

  'rating.Great': 'Great',
  'rating.Good': 'Good',
  'rating.OK': 'OK',
  'rating.Poor': 'Poor',
  'rating.Excellent': 'Excellent',
  'rating.Acceptable': 'Acceptable',
  'rating.NA': 'N/A',
  'priority.Critical': 'Critical',
  'priority.Important': 'Important',
  'priority.NiceToHave': 'Nice to Have',
  'priority.DontCare': "Don't Care",

  'eval.selectAnOption': 'Select an Option',

  'section.Features': 'Features',
  'section.Dimensions': 'Dimensions',
  'section.Requirements': 'Requirements',
  'section.Products': 'Products',
  'section.ImpactByDimension': 'Impact By Dimension',
  'section.ImpactByRequirement': 'Impact By Requirement',

  'eval.createNew': 'Create new',
  'eval.addDimension': 'Add dimension',
  'eval.addRequirement': 'Add requirement',
  'eval.addFeature': 'Add feature',
  'eval.rank': 'Rank',
  'eval.rankExplanation': 'Rank Explanation',
  'eval.criticalFeatures': 'Critical features',
  'eval.importantFeatures': 'Important features',
  'eval.meets': 'Meets',
  'eval.doesNotMeet': 'Does not meet',
  'eval.ratingLabel': 'Rating',
  'eval.reasoning': 'Reasoning',
  'eval.reasoningPlaceholder': 'Describe your reason for this change',
  'eval.reasoningWarning': 'Rating changed and the prior reasoning might not be relevant any more.',

  'eval.tabReport': 'Report',
  'eval.tabTables': 'Tables',
  'eval.tabQuestions': 'Questions',
  'eval.share': 'Share',
  'eval.rename': 'Rename',
  'eval.copyLink': 'Copy link',
  'eval.downloadReport': 'Download report',
  'eval.downloadTables': 'Download tables',
  'eval.downloadScoreDetail': 'Download score detail',
  'eval.archive': 'Archive',
  'eval.unarchive': 'Unarchive',
  'eval.versions': 'Versions',
  'eval.newVersion': 'New Version',

  'chart.meetsAll': 'Meets all critical and important features',
  'chart.meetsCriticalNotImportant': 'Meets all critical, but does not meet all important features',
  'chart.meetsImportantNotCritical': 'Does not meet all critical, but meets all important features',
  'chart.meetsNone': 'Does not meet all critical and important features',
  'chart.noProducts': 'There are no products to show here.',

  'eval.batchMode': 'Batch mode',
  'eval.batchChanges': 'Batch changes',
  'eval.batchModeActive': 'Batch Mode Active',
  'eval.batchModeExitFirst': 'Please exit batch mode first.',

  'eval.disconnected': 'Disconnected',
  'eval.repairing': 'Repairing...',
  'eval.restoring': 'Restoring...',
  'eval.syncing': 'Syncing...',
  'eval.updatingCount': 'updating...',
  'eval.updatesFailed': 'failed',
  'eval.updated': 'Updated',

  'eval.outOfFreeChanges': "You're out of free changes to your evaluation. ",
  'eval.upgradeToUnlock': 'Upgrade now to unlock full access.',

  'eval.addAnother': 'Add another',

  'comparison.dimension': 'Dimension',
  'comparison.requirement': 'Requirement',
  'comparison.feature': 'Feature',
  'comparison.weight': 'Weight(%)',
  'comparison.priority': 'Priority',
  'comparison.impact': 'Impact',
  'comparison.selectType': 'Select a type',
  'comparison.selectProduct': 'Select a product',
  'comparison.noRows': 'No rows found. Please try updating your selections.',
  'comparison.comparison': 'comparison',

  'sidebar.contents': 'Contents',
  'sidebar.tables': 'Tables',
  'sidebar.questions': 'Questions',
  'sidebar.changeLog': 'Change log',

  'batch.enterTitle': 'Enter Batch Mode?',
  'batch.enterDescription': 'In batch mode, changes you and others make will not update the underlying reports until you exit batch mode. This is useful when making many changes at once. All users will see this mode change.',
  'batch.enterConfirm': 'Batch',
  'batch.cancelTitle': 'Cancel Batch Mode?',
  'batch.cancelDescription': 'This will discard all changes made during batch mode and revert to the snapshot taken when you entered batch mode. This action cannot be undone. This will affect all users.',
  'batch.cancelConfirm': 'Cancel Batch (Discard Changes)',

  'filter.filteringBy': 'Filtering by',
  'filter.selectAll': 'Select All',
  'filter.deselectAll': 'Deselect All',
  'filter.noContent': 'No content available. Try adding new data or update your filters.',

  'error.somethingWentWrong': 'Something went wrong',
  'error.tryAgainOrRefresh': 'Please try again or refresh the page.',
  'error.contactSupport': 'If the problem persists, please contact support.',
  'error.pageNotFound': 'Page not found',
  'error.returnToDashboard': 'Return to dashboard',
  'error.accessDenied': 'Access denied',
  'error.accessDeniedDescription': 'You do not have permission to access this resource.',

  'status.viewOnly': 'View only',
  'status.linkCopied': 'Link copied.',
  'status.noneAvailable': 'None available',
  'status.saved': 'Saved',
  'status.saving': 'Saving...',
  'status.connecting': 'Connecting...',
  'status.connected': 'Connected',

  'workspace.parentWorkspace': 'Parent Workspace',
  'workspace.projectCode': 'Project Code',
  'workspace.createNew': 'Create new workspace',
  'workspace.name': 'Workspace name',
  'workspace.editWorkspace': 'Edit workspace',
  'workspace.deleteWorkspace': 'Delete workspace',
  'workspace.deleteConfirm': 'Are you sure you want to delete this workspace?',
  'workspace.inviteMembers': 'Invite members',
  'workspace.role': 'Role',
  'workspace.admin': 'Admin',
  'workspace.member': 'Member',
  'workspace.viewer': 'Viewer',
  'workspace.removeMember': 'Remove member',
  'workspace.pendingInvites': 'Pending invites',
  'workspace.noMembers': 'No members yet',
  'workspace.workspaceId': 'Workspace ID',

  'invite.emailPlaceholder': 'Enter email address',
  'invite.sendInvite': 'Send invite',
  'invite.inviteSent': 'Invite sent',
  'invite.inviteUsers': 'Invite users',
  'invite.revokeInvite': 'Revoke invite',

  'toast.linkCopied': 'Link copied.',
  'toast.changesSaved': 'Changes saved.',
  'toast.evaluationArchived': 'Evaluation archived.',
  'toast.evaluationUnarchived': 'Evaluation unarchived.',
  'toast.evaluationDeleted': 'Evaluation deleted.',
  'toast.evaluationRenamed': 'Evaluation renamed.',
  'toast.batchModeActive': 'Batch mode is active. Changes will be applied when you exit batch mode.',
  'toast.switchedToEach': 'Switched to individual mode.',
  'toast.errorOccurred': 'An error occurred.',
  'toast.archivingEvaluation': 'Archiving evaluation.',
  'toast.unarchivingEvaluation': 'Unarchiving evaluation.',
  'toast.deletingEvaluation': 'Deleting evaluation.',
  'toast.creatingVersion': 'Creating new version.',
  'toast.versionCreated': 'New version created.',
  'confirm.deleteEvalTitle': 'Are you sure you want to delete this evaluation?',
  'confirm.deleteEvalDescription': 'Do you want to completely delete this evaluation? All related data will be removed and cannot be restored.',
  'confirm.recoverEvalTitle': 'Recover evaluation from database',
  'confirm.recoverEvalDescription': 'CAUTION: This will re-pull the evaluation from the database. Current unsaved changes might be lost.',
  'changelog.tooltip': 'Change log',
  'changelog.batchWarning': 'Changes will not register in the change log while you are in',
  'changelog.batchMode': 'Batch mode',

  'editor.pasteLink': 'Paste link',
  'editor.textToDisplay': 'Text to display',
  'editor.visitLink': 'Visit link',
  'editor.editLink': 'Edit link',
  'editor.removeLink': 'Remove link',
  'editor.link': 'Link',
  'editor.comment': 'Comment',
  'editor.bold': 'Bold',
  'editor.italic': 'Italic',
  'editor.underline': 'Underline',
  'editor.editComment': 'Edit comment',
  'editor.deleteComment': 'Delete comment',

  // Account / Profile
  'account.emailAddress': 'Email address',
  'account.currentEmail': 'Current email',
  'account.emailVerify': 'We will email you to verify the change.',
  'account.confirmNewPassword': 'Confirm new password',
  'account.savePassword': 'Save password',
  'account.updatingProfile': 'Updating profile.',
  'account.profileUpdated': 'Profile updated successfully.',
  'account.profileUpdateError': 'Something went wrong while updating your profile. Please try again or contact support.',
  'account.updatingPassword': 'Updating password.',
}

const es: TranslationKeys = {
  'nav.dashboard': 'Panel',
  'nav.evaluations': 'Evaluaciones',
  'nav.account': 'Cuenta',
  'nav.settings': 'Configuración',
  'nav.logout': 'Cerrar sesión',
  'nav.workspace': 'Espacio de trabajo',
  'nav.manageAccount': 'Administrar cuenta',
  'nav.manageWorkspace': 'Administrar espacio de trabajo',
  'nav.playWalkthrough': 'Ver tutorial',
  'nav.documentation': 'Documentación',
  'nav.support': 'Soporte',
  'nav.talkToExpert': 'Hablar con un experto',

  'account.title': 'Mi cuenta',
  'account.myProfile': 'Mi perfil',
  'account.preferences': 'Preferencias',
  'account.security': 'Seguridad de la cuenta',
  'account.language': 'Idioma',
  'account.email': 'Correo electrónico',
  'account.password': 'Contraseña',
  'account.loginConnections': 'Conexiones de inicio de sesión',
  'account.firstName': 'Nombre',
  'account.lastName': 'Apellido',
  'account.changePassword': 'Cambiar contraseña',
  'account.currentPassword': 'Contraseña actual',
  'account.newPassword': 'Nueva contraseña',
  'account.confirmPassword': 'Confirmar contraseña',

  'action.save': 'Guardar',
  'action.cancel': 'Cancelar',
  'action.delete': 'Eliminar',
  'action.edit': 'Editar',
  'action.create': 'Crear',
  'action.close': 'Cerrar',
  'action.confirm': 'Confirmar',
  'action.search': 'Buscar',
  'action.filter': 'Filtrar',
  'action.export': 'Exportar',
  'action.import': 'Importar',
  'action.refresh': 'Actualizar',
  'action.back': 'Atrás',
  'action.next': 'Siguiente',
  'action.previous': 'Anterior',
  'action.submit': 'Enviar',
  'action.reset': 'Restablecer',
  'action.update': 'Actualizar',
  'action.add': 'Agregar',
  'action.remove': 'Quitar',
  'action.copy': 'Copiar',
  'action.download': 'Descargar',
  'action.share': 'Compartir',
  'action.invite': 'Invitar',
  'action.retry': 'Intentar de nuevo',
  'action.reload': 'Recargar',
  'action.saving': 'Guardando...',
  'action.ok': 'Aceptar',

  'dashboard.title': 'Panel',
  'dashboard.recentEvaluations': 'Evaluaciones recientes',
  'dashboard.noEvaluations': 'Aún no hay evaluaciones',
  'dashboard.createEvaluation': 'Crear evaluación',
  'dashboard.lastUpdated': 'Última actualización',
  'dashboard.status': 'Estado',
  'dashboard.viewAll': 'Ver todo',
  'dashboard.gettingStarted': 'Primeros pasos',
  'dashboard.myEvaluations': 'Mis evaluaciones',
  'dashboard.shared': 'Compartidas',
  'dashboard.archived': 'Archivadas',
  'dashboard.backToAccount': 'Volver a la cuenta',
  'dashboard.backToDashboard': 'Volver al panel',
  'dashboard.emptyOwned': 'Las evaluaciones que cree o reciba aparecerán aquí.',
  'dashboard.emptyShared': 'Las evaluaciones en las que sea colaborador aparecerán aquí.',
  'dashboard.emptyArchived': 'Las evaluaciones archivadas aparecerán aquí.',
  'dashboard.createFirst': 'Crear mi primera evaluación',
  'dashboard.watchWalkthrough': 'Ver tutorial',
  'dashboard.watchVideo': 'Vea nuestro video de introducción',
  'dashboard.createFirstEval': 'Cree su primera evaluación',
  'dashboard.scheduleCall': 'Agendar una llamada',
  'dashboard.bookExpert': 'Reserve tiempo con uno de nuestros expertos',

  'eval.title': 'Evaluación',
  'eval.newEvaluation': 'Nueva evaluación',
  'eval.products': 'Productos',
  'eval.scores': 'Puntuaciones',
  'eval.introduction': 'Introducción',
  'eval.conclusion': 'Conclusión',
  'eval.changeLogs': 'Registro de cambios',
  'eval.template': 'Plantilla',
  'eval.status.draft': 'Borrador',
  'eval.status.active': 'Activo',
  'eval.status.archived': 'Archivado',
  'eval.status.completed': 'Completado',
  'eval.recover': 'Recuperar',
  'eval.updating': 'Actualizando...',
  'eval.loading': 'Cargando...',

  'label.name': 'Nombre',
  'label.description': 'Descripción',
  'label.date': 'Fecha',
  'label.type': 'Tipo',
  'label.category': 'Categoría',
  'label.vendor': 'Proveedor',
  'label.product': 'Producto',
  'label.score': 'Puntuación',
  'label.rating': 'Calificación',
  'label.notes': 'Notas',
  'label.comments': 'Comentarios',
  'label.createdAt': 'Creado',
  'label.updatedAt': 'Actualizado',
  'label.createdBy': 'Creado por',
  'label.all': 'Todos',
  'label.none': 'Ninguno',
  'label.loading': 'Cargando...',
  'label.noResults': 'No se encontraron resultados',
  'label.error': 'Ocurrió un error',
  'label.success': 'Éxito',
  'label.warning': 'Advertencia',
  'label.lastModified': 'Última modificación',
  'label.owner': 'Propietario',
  'label.collaborators': 'Colaboradores',

  'evalCreate.categoryHeading': 'Ahorre más de 20 horas de investigación en 5 minutos',
  'evalCreate.categorySubtext': 'Por favor, seleccione una categoría de producto de la lista a continuación.',
  'evalCreate.categorySearch': 'Buscar o hacer clic en una opción',
  'evalCreate.categoryNoProducts': 'No se encontraron productos para esta categoría.',
  'evalCreate.categoryStale': 'Esta categoría tiene más de 90 días de antigüedad.',
  'evalCreate.categoryRequestRefresh': 'Solicitar una actualización',
  'evalCreate.categoryProductsIncluded': 'Los siguientes productos están incluidos:',
  'evalCreate.categoryNotFound': '¿No encuentra lo que necesita?',
  'evalCreate.useCaseHeading': '¿Qué casos de uso son relevantes para usted?',
  'evalCreate.useCaseSubtext': 'Seleccionamos algunos casos de uso fundamentales para comenzar, pero puede agregar tantos como desee.',
  'evalCreate.useCaseSearch': 'Buscar casos de uso',
  'evalCreate.useCaseSelectAll': 'Seleccionar todos',
  'evalCreate.useCaseDeselectAll': 'Deseleccionar todos',
  'evalCreate.useCaseSelected': 'Casos de uso seleccionados',
  'evalCreate.useCaseLooksGood': 'Se ve bien',
  'evalCreate.useCaseNone': 'No se encontró ningún caso de uso.',
  'evalCreate.useCaseNotFound': 'No se encontraron casos de uso para esta categoría.',
  'evalCreate.requestNewCategory': 'Solicitar una nueva categoría',
  'evalCreate.categoryPanelHeading': '¿No ves algo que necesitas?',
  'evalCreate.categoryPanelSubtext': 'Podemos investigar una nueva categoría para ti en 2 días hábiles.',

  'workspace.manage': 'Administrar espacio de trabajo',
  'workspace.general': 'General',
  'workspace.members': 'Miembros',
  'workspace.billing': 'Facturación',

  'rating.Great': 'Excelente',
  'rating.Good': 'Bueno',
  'rating.OK': 'Aceptable',
  'rating.Poor': 'Deficiente',
  'rating.Excellent': 'Excelente',
  'rating.Acceptable': 'Aceptable',
  'rating.NA': 'N/D',
  'priority.Critical': 'Crítico',
  'priority.Important': 'Importante',
  'priority.NiceToHave': 'Deseable',
  'priority.DontCare': 'No importa',

  'eval.selectAnOption': 'Seleccionar una opción',

  'section.Features': 'Características',
  'section.Dimensions': 'Dimensiones',
  'section.Requirements': 'Requisitos',
  'section.Products': 'Productos',
  'section.ImpactByDimension': 'Impacto por dimensión',
  'section.ImpactByRequirement': 'Impacto por requisito',

  'eval.createNew': 'Crear nuevo',
  'eval.addDimension': 'Agregar dimensión',
  'eval.addRequirement': 'Agregar requisito',
  'eval.addFeature': 'Agregar característica',
  'eval.rank': 'Clasificación',
  'eval.rankExplanation': 'Explicación de clasificación',
  'eval.criticalFeatures': 'Características críticas',
  'eval.importantFeatures': 'Características importantes',
  'eval.meets': 'Cumple',
  'eval.doesNotMeet': 'No cumple',
  'eval.ratingLabel': 'Calificación',
  'eval.reasoning': 'Razonamiento',
  'eval.reasoningPlaceholder': 'Describa la razón de este cambio',
  'eval.reasoningWarning': 'La calificación cambió y el razonamiento anterior podría no ser relevante.',

  'eval.tabReport': 'Informe',
  'eval.tabTables': 'Tablas',
  'eval.tabQuestions': 'Preguntas',
  'eval.share': 'Compartir',
  'eval.rename': 'Renombrar',
  'eval.copyLink': 'Copiar enlace',
  'eval.downloadReport': 'Descargar informe',
  'eval.downloadTables': 'Descargar tablas',
  'eval.downloadScoreDetail': 'Descargar detalle de puntuación',
  'eval.archive': 'Archivar',
  'eval.unarchive': 'Desarchivar',
  'eval.versions': 'Versiones',
  'eval.newVersion': 'Nueva versión',

  'chart.meetsAll': 'Cumple con todas las características críticas e importantes',
  'chart.meetsCriticalNotImportant': 'Cumple con todas las críticas, pero no con todas las importantes',
  'chart.meetsImportantNotCritical': 'No cumple con todas las críticas, pero sí con todas las importantes',
  'chart.meetsNone': 'No cumple con todas las características críticas e importantes',
  'chart.noProducts': 'No hay productos para mostrar aquí.',

  'eval.batchMode': 'Modo por lotes',
  'eval.batchChanges': 'Cambios por lotes',
  'eval.batchModeActive': 'Modo por lotes activo',
  'eval.batchModeExitFirst': 'Por favor, salga del modo por lotes primero.',

  'eval.disconnected': 'Desconectado',
  'eval.repairing': 'Reparando...',
  'eval.restoring': 'Restaurando...',
  'eval.syncing': 'Sincronizando...',
  'eval.updatingCount': 'actualizando...',
  'eval.updatesFailed': 'fallido(s)',
  'eval.updated': 'Actualizado',

  'eval.outOfFreeChanges': 'Se han agotado los cambios gratuitos de su evaluación. ',
  'eval.upgradeToUnlock': 'Actualice ahora para desbloquear el acceso completo.',

  'eval.addAnother': 'Agregar otro',

  'comparison.dimension': 'Dimensión',
  'comparison.requirement': 'Requisito',
  'comparison.feature': 'Característica',
  'comparison.weight': 'Peso(%)',
  'comparison.priority': 'Prioridad',
  'comparison.impact': 'Impacto',
  'comparison.selectType': 'Seleccionar un tipo',
  'comparison.selectProduct': 'Seleccionar un producto',
  'comparison.noRows': 'No se encontraron filas. Intente actualizar sus selecciones.',
  'comparison.comparison': 'comparación',

  'sidebar.contents': 'Contenido',
  'sidebar.tables': 'Tablas',
  'sidebar.questions': 'Preguntas',
  'sidebar.changeLog': 'Registro de cambios',

  'batch.enterTitle': '¿Entrar en modo por lotes?',
  'batch.enterDescription': 'En modo por lotes, los cambios que usted y otros realicen no actualizarán los informes subyacentes hasta que salga del modo por lotes. Esto es útil cuando se realizan muchos cambios a la vez. Todos los usuarios verán este cambio de modo.',
  'batch.enterConfirm': 'Lotes',
  'batch.cancelTitle': '¿Cancelar modo por lotes?',
  'batch.cancelDescription': 'Esto descartará todos los cambios realizados durante el modo por lotes y revertirá a la instantánea tomada cuando ingresó al modo por lotes. Esta acción no se puede deshacer. Esto afectará a todos los usuarios.',
  'batch.cancelConfirm': 'Cancelar lotes (Descartar cambios)',

  'filter.filteringBy': 'Filtrar por',
  'filter.selectAll': 'Seleccionar todo',
  'filter.deselectAll': 'Deseleccionar todo',
  'filter.noContent': 'No hay contenido disponible. Intente agregar nuevos datos o actualizar sus filtros.',

  'error.somethingWentWrong': 'Algo salió mal',
  'error.tryAgainOrRefresh': 'Por favor, inténtelo de nuevo o actualice la página.',
  'error.contactSupport': 'Si el problema persiste, contacte con soporte.',
  'error.pageNotFound': 'Página no encontrada',
  'error.returnToDashboard': 'Volver al panel',
  'error.accessDenied': 'Acceso denegado',
  'error.accessDeniedDescription': 'No tiene permiso para acceder a este recurso.',

  'status.viewOnly': 'Solo lectura',
  'status.linkCopied': 'Enlace copiado.',
  'status.noneAvailable': 'No disponible',
  'status.saved': 'Guardado',
  'status.saving': 'Guardando...',
  'status.connecting': 'Conectando...',
  'status.connected': 'Conectado',

  'workspace.parentWorkspace': 'Espacio de trabajo principal',
  'workspace.projectCode': 'Código de proyecto',
  'workspace.createNew': 'Crear nuevo espacio de trabajo',
  'workspace.name': 'Nombre del espacio de trabajo',
  'workspace.editWorkspace': 'Editar espacio de trabajo',
  'workspace.deleteWorkspace': 'Eliminar espacio de trabajo',
  'workspace.deleteConfirm': '¿Está seguro de que desea eliminar este espacio de trabajo?',
  'workspace.inviteMembers': 'Invitar miembros',
  'workspace.role': 'Rol',
  'workspace.admin': 'Administrador',
  'workspace.member': 'Miembro',
  'workspace.viewer': 'Visualizador',
  'workspace.removeMember': 'Eliminar miembro',
  'workspace.pendingInvites': 'Invitaciones pendientes',
  'workspace.noMembers': 'Aún no hay miembros',
  'workspace.workspaceId': 'ID del espacio de trabajo',
  'workspace.billingPlan': 'Plan',
  'workspace.billingPaymentInfo': 'Información de pago',
  'workspace.billingHistory': 'Historial de facturación',

  'invite.emailPlaceholder': 'Ingrese dirección de correo',
  'invite.sendInvite': 'Enviar invitación',
  'invite.inviteSent': 'Invitación enviada',
  'invite.inviteUsers': 'Invitar usuarios',
  'invite.revokeInvite': 'Revocar invitación',

  'toast.linkCopied': 'Enlace copiado.',
  'toast.changesSaved': 'Cambios guardados.',
  'toast.evaluationArchived': 'Evaluación archivada.',
  'toast.evaluationUnarchived': 'Evaluación desarchivada.',
  'toast.evaluationDeleted': 'Evaluación eliminada.',
  'toast.evaluationRenamed': 'Evaluación renombrada.',
  'toast.batchModeActive': 'Modo por lotes activo. Los cambios se aplicarán al salir del modo por lotes.',
  'toast.switchedToEach': 'Cambiado a modo individual.',
  'toast.errorOccurred': 'Ocurrió un error.',
  'toast.archivingEvaluation': 'Archivando evaluación.',
  'toast.unarchivingEvaluation': 'Desarchivando evaluación.',
  'toast.deletingEvaluation': 'Eliminando evaluación.',
  'toast.creatingVersion': 'Creando nueva versión.',
  'toast.versionCreated': 'Nueva versión creada.',
  'confirm.deleteEvalTitle': '¿Está seguro de que desea eliminar esta evaluación?',
  'confirm.deleteEvalDescription': '¿Desea eliminar completamente esta evaluación? Todos los datos relacionados se eliminarán y no se podrán restaurar.',
  'confirm.recoverEvalTitle': 'Recuperar evaluación de la base de datos',
  'confirm.recoverEvalDescription': 'PRECAUCIÓN: Esto volverá a obtener la evaluación de la base de datos. Los cambios no guardados podrían perderse.',
  'changelog.tooltip': 'Registro de cambios',
  'changelog.batchWarning': 'Los cambios no se registrarán en el registro de cambios mientras esté en',
  'changelog.batchMode': 'Modo por lotes',

  'editor.pasteLink': 'Pegar enlace',
  'editor.textToDisplay': 'Texto a mostrar',
  'editor.visitLink': 'Visitar enlace',
  'editor.editLink': 'Editar enlace',
  'editor.removeLink': 'Eliminar enlace',
  'editor.link': 'Enlace',
  'editor.comment': 'Comentario',
  'editor.bold': 'Negrita',
  'editor.italic': 'Cursiva',
  'editor.underline': 'Subrayado',
  'editor.editComment': 'Editar comentario',
  'editor.deleteComment': 'Eliminar comentario',

  // Account / Profile
  'account.emailAddress': 'Dirección de correo electrónico',
  'account.currentEmail': 'Correo electrónico actual',
  'account.emailVerify': 'Le enviaremos un correo electrónico para verificar el cambio.',
  'account.confirmNewPassword': 'Confirmar nueva contraseña',
  'account.savePassword': 'Guardar contraseña',
  'account.updatingProfile': 'Actualizando perfil.',
  'account.profileUpdated': 'Perfil actualizado correctamente.',
  'account.profileUpdateError': 'Algo salió mal al actualizar su perfil. Por favor, inténtelo de nuevo o contacte con soporte.',
  'account.updatingPassword': 'Actualizando contraseña.',
}

const fr: TranslationKeys = {
  'nav.dashboard': 'Tableau de bord',
  'nav.evaluations': 'Évaluations',
  'nav.account': 'Compte',
  'nav.settings': 'Paramètres',
  'nav.logout': 'Déconnexion',
  'nav.workspace': 'Espace de travail',
  'nav.manageAccount': 'Gérer le compte',
  'nav.manageWorkspace': "Gérer l'espace de travail",
  'nav.playWalkthrough': 'Voir le tutoriel',
  'nav.documentation': 'Documentation',
  'nav.support': 'Assistance',
  'nav.talkToExpert': "Parler à un expert",

  'account.title': 'Mon compte',
  'account.myProfile': 'Mon profil',
  'account.preferences': 'Préférences',
  'account.security': 'Sécurité du compte',
  'account.language': 'Langue',
  'account.email': 'E-mail',
  'account.password': 'Mot de passe',
  'account.loginConnections': 'Connexions',
  'account.firstName': 'Prénom',
  'account.lastName': 'Nom',
  'account.changePassword': 'Changer le mot de passe',
  'account.currentPassword': 'Mot de passe actuel',
  'account.newPassword': 'Nouveau mot de passe',
  'account.confirmPassword': 'Confirmer le mot de passe',

  'action.save': 'Enregistrer',
  'action.cancel': 'Annuler',
  'action.delete': 'Supprimer',
  'action.edit': 'Modifier',
  'action.create': 'Créer',
  'action.close': 'Fermer',
  'action.confirm': 'Confirmer',
  'action.search': 'Rechercher',
  'action.filter': 'Filtrer',
  'action.export': 'Exporter',
  'action.import': 'Importer',
  'action.refresh': 'Actualiser',
  'action.back': 'Retour',
  'action.next': 'Suivant',
  'action.previous': 'Précédent',
  'action.submit': 'Soumettre',
  'action.reset': 'Réinitialiser',
  'action.update': 'Mettre à jour',
  'action.add': 'Ajouter',
  'action.remove': 'Supprimer',
  'action.copy': 'Copier',
  'action.download': 'Télécharger',
  'action.share': 'Partager',
  'action.invite': 'Inviter',
  'action.retry': 'Réessayer',
  'action.reload': 'Recharger',
  'action.saving': 'Enregistrement...',
  'action.ok': 'OK',

  'dashboard.title': 'Tableau de bord',
  'dashboard.recentEvaluations': 'Évaluations récentes',
  'dashboard.noEvaluations': 'Aucune évaluation',
  'dashboard.createEvaluation': 'Créer une évaluation',
  'dashboard.lastUpdated': 'Dernière mise à jour',
  'dashboard.status': 'Statut',
  'dashboard.viewAll': 'Voir tout',
  'dashboard.gettingStarted': 'Premiers pas',
  'dashboard.myEvaluations': 'Mes évaluations',
  'dashboard.shared': 'Partagées',
  'dashboard.archived': 'Archivées',
  'dashboard.backToAccount': 'Retour au compte',
  'dashboard.backToDashboard': 'Retour au tableau de bord',
  'dashboard.emptyOwned': 'Les évaluations que vous créez ou recevez apparaîtront ici.',
  'dashboard.emptyShared': 'Les évaluations dont vous êtes collaborateur apparaîtront ici.',
  'dashboard.emptyArchived': 'Les évaluations archivées apparaîtront ici.',
  'dashboard.createFirst': 'Créer ma première évaluation',
  'dashboard.watchWalkthrough': 'Voir le tutoriel',
  'dashboard.watchVideo': 'Regardez notre vidéo de démarrage',
  'dashboard.createFirstEval': 'Créez votre première évaluation',
  'dashboard.scheduleCall': 'Planifier un appel',
  'dashboard.bookExpert': "Réservez du temps avec l'un de nos experts",

  'eval.title': 'Évaluation',
  'eval.newEvaluation': 'Nouvelle évaluation',
  'eval.products': 'Produits',
  'eval.scores': 'Scores',
  'eval.introduction': 'Introduction',
  'eval.conclusion': 'Conclusion',
  'eval.changeLogs': 'Journal des modifications',
  'eval.template': 'Modèle',
  'eval.status.draft': 'Brouillon',
  'eval.status.active': 'Actif',
  'eval.status.archived': 'Archivé',
  'eval.status.completed': 'Terminé',
  'eval.recover': 'Récupérer',
  'eval.updating': 'Mise à jour...',
  'eval.loading': 'Chargement...',

  'label.name': 'Nom',
  'label.description': 'Description',
  'label.date': 'Date',
  'label.type': 'Type',
  'label.category': 'Catégorie',
  'label.vendor': 'Fournisseur',
  'label.product': 'Produit',
  'label.score': 'Score',
  'label.rating': 'Note',
  'label.notes': 'Notes',
  'label.comments': 'Commentaires',
  'label.createdAt': 'Créé',
  'label.updatedAt': 'Mis à jour',
  'label.createdBy': 'Créé par',
  'label.all': 'Tout',
  'label.none': 'Aucun',
  'label.loading': 'Chargement...',
  'label.noResults': 'Aucun résultat',
  'label.error': 'Une erreur est survenue',
  'label.success': 'Succès',
  'label.warning': 'Avertissement',
  'label.lastModified': 'Dernière modification',
  'label.owner': 'Propriétaire',
  'label.collaborators': 'Collaborateurs',

  'evalCreate.categoryHeading': 'Économisez plus de 20 heures de recherche en 5 minutes',
  'evalCreate.categorySubtext': 'Veuillez sélectionner une catégorie de produit dans la liste ci-dessous.',
  'evalCreate.categorySearch': 'Rechercher ou cliquer sur une option',
  'evalCreate.categoryNoProducts': 'Aucun produit trouvé pour cette catégorie.',
  'evalCreate.categoryStale': 'Cette catégorie a plus de 90 jours.',
  'evalCreate.categoryRequestRefresh': 'Demander une actualisation',
  'evalCreate.categoryProductsIncluded': 'Les produits suivants sont inclus :',
  'evalCreate.categoryNotFound': 'Vous ne trouvez pas ce dont vous avez besoin ?',
  'evalCreate.useCaseHeading': 'Quels cas d\'utilisation sont pertinents pour vous ?',
  'evalCreate.useCaseSubtext': 'Nous avons sélectionné quelques cas d\'utilisation fondamentaux pour commencer, mais vous pouvez en ajouter autant que vous le souhaitez.',
  'evalCreate.useCaseSearch': 'Rechercher des cas d\'utilisation',
  'evalCreate.useCaseSelectAll': 'Tout sélectionner',
  'evalCreate.useCaseDeselectAll': 'Tout désélectionner',
  'evalCreate.useCaseSelected': 'Cas d\'utilisation sélectionnés',
  'evalCreate.useCaseLooksGood': 'C\'est bon',
  'evalCreate.useCaseNone': 'Aucun cas d\'utilisation trouvé.',
  'evalCreate.useCaseNotFound': 'Aucun cas d\'utilisation trouvé pour cette catégorie.',
  'evalCreate.requestNewCategory': 'Demander une nouvelle catégorie',
  'evalCreate.categoryPanelHeading': 'Vous ne trouvez pas ce dont vous avez besoin ?',
  'evalCreate.categoryPanelSubtext': 'Nous pouvons rechercher une nouvelle catégorie pour vous en 2 jours ouvrables.',

  'workspace.manage': "Gérer l'espace de travail",
  'workspace.general': 'Général',
  'workspace.members': 'Membres',
  'workspace.billing': 'Facturation',

  'rating.Great': 'Excellent',
  'rating.Good': 'Bon',
  'rating.OK': 'Acceptable',
  'rating.Poor': 'Médiocre',
  'rating.Excellent': 'Excellent',
  'rating.Acceptable': 'Acceptable',
  'rating.NA': 'S/O',
  'priority.Critical': 'Critique',
  'priority.Important': 'Important',
  'priority.NiceToHave': 'Souhaitable',
  'priority.DontCare': 'Sans importance',

  'eval.selectAnOption': 'Sélectionner une option',

  'section.Features': 'Fonctionnalités',
  'section.Dimensions': 'Dimensions',
  'section.Requirements': 'Exigences',
  'section.Products': 'Produits',
  'section.ImpactByDimension': 'Impact par dimension',
  'section.ImpactByRequirement': 'Impact par exigence',

  'eval.createNew': 'Créer nouveau',
  'eval.addDimension': 'Ajouter une dimension',
  'eval.addRequirement': 'Ajouter une exigence',
  'eval.addFeature': 'Ajouter une fonctionnalité',
  'eval.rank': 'Classement',
  'eval.rankExplanation': 'Explication du classement',
  'eval.criticalFeatures': 'Fonctionnalités critiques',
  'eval.importantFeatures': 'Fonctionnalités importantes',
  'eval.meets': 'Satisfait',
  'eval.doesNotMeet': 'Ne satisfait pas',
  'eval.ratingLabel': 'Note',
  'eval.reasoning': 'Raisonnement',
  'eval.reasoningPlaceholder': 'Décrivez la raison de ce changement',
  'eval.reasoningWarning': 'La note a changé et le raisonnement précédent pourrait ne plus être pertinent.',

  'eval.tabReport': 'Rapport',
  'eval.tabTables': 'Tableaux',
  'eval.tabQuestions': 'Questions',
  'eval.share': 'Partager',
  'eval.rename': 'Renommer',
  'eval.copyLink': 'Copier le lien',
  'eval.downloadReport': 'Télécharger le rapport',
  'eval.downloadTables': 'Télécharger les tableaux',
  'eval.downloadScoreDetail': 'Télécharger le détail des scores',
  'eval.archive': 'Archiver',
  'eval.unarchive': 'Désarchiver',
  'eval.versions': 'Versions',
  'eval.newVersion': 'Nouvelle version',

  'chart.meetsAll': 'Satisfait toutes les fonctionnalités critiques et importantes',
  'chart.meetsCriticalNotImportant': 'Satisfait toutes les critiques, mais pas toutes les importantes',
  'chart.meetsImportantNotCritical': 'Ne satisfait pas toutes les critiques, mais satisfait toutes les importantes',
  'chart.meetsNone': 'Ne satisfait pas toutes les fonctionnalités critiques et importantes',
  'chart.noProducts': 'Aucun produit à afficher ici.',

  'eval.batchMode': 'Mode par lots',
  'eval.batchChanges': 'Modifications par lots',
  'eval.batchModeActive': 'Mode par lots actif',
  'eval.batchModeExitFirst': 'Veuillez quitter le mode par lots d\'abord.',

  'eval.disconnected': 'Déconnecté',
  'eval.repairing': 'Réparation...',
  'eval.restoring': 'Restauration...',
  'eval.syncing': 'Synchronisation...',
  'eval.updatingCount': 'mise à jour...',
  'eval.updatesFailed': 'échoué(s)',
  'eval.updated': 'Mis à jour',

  'eval.outOfFreeChanges': 'Vous avez épuisé vos modifications gratuites. ',
  'eval.upgradeToUnlock': 'Passez à la version supérieure pour un accès complet.',

  'eval.addAnother': 'Ajouter un autre',

  'comparison.dimension': 'Dimension',
  'comparison.requirement': 'Exigence',
  'comparison.feature': 'Fonctionnalité',
  'comparison.weight': 'Poids(%)',
  'comparison.priority': 'Priorité',
  'comparison.impact': 'Impact',
  'comparison.selectType': 'Sélectionner un type',
  'comparison.selectProduct': 'Sélectionner un produit',
  'comparison.noRows': 'Aucune ligne trouvée. Essayez de mettre à jour vos sélections.',
  'comparison.comparison': 'comparaison',

  'sidebar.contents': 'Sommaire',
  'sidebar.tables': 'Tableaux',
  'sidebar.questions': 'Questions',
  'sidebar.changeLog': 'Journal des modifications',

  'batch.enterTitle': 'Entrer en mode par lots ?',
  'batch.enterDescription': 'En mode par lots, les modifications que vous et les autres effectuez ne mettront pas à jour les rapports sous-jacents jusqu\'à ce que vous quittiez le mode par lots. Ceci est utile lorsque vous effectuez de nombreuses modifications à la fois. Tous les utilisateurs verront ce changement de mode.',
  'batch.enterConfirm': 'Lots',
  'batch.cancelTitle': 'Annuler le mode par lots ?',
  'batch.cancelDescription': 'Cela supprimera toutes les modifications effectuées pendant le mode par lots et reviendra à l\'instantané pris lorsque vous êtes entré en mode par lots. Cette action ne peut pas être annulée. Cela affectera tous les utilisateurs.',
  'batch.cancelConfirm': 'Annuler les lots (Supprimer les modifications)',

  'filter.filteringBy': 'Filtrer par',
  'filter.selectAll': 'Tout sélectionner',
  'filter.deselectAll': 'Tout désélectionner',
  'filter.noContent': 'Aucun contenu disponible. Essayez d\'ajouter de nouvelles données ou de mettre à jour vos filtres.',

  'error.somethingWentWrong': 'Oups ! Quelque chose a mal tourné.',
  'error.tryAgainOrRefresh': 'Réessayez ou actualisez la page. Si le problème persiste, contactez-nous.',
  'error.contactSupport': 'Veuillez contacter le support.',
  'error.pageNotFound': 'Page non trouvée',
  'error.returnToDashboard': 'Retour au tableau de bord',

  'status.viewOnly': 'Lecture seule',
  'status.linkCopied': 'Lien copié.',
  'status.noneAvailable': 'Aucun disponible',
  'error.accessDenied': 'Accès refusé',
  'error.accessDeniedDescription': 'Vous n\'avez pas la permission d\'accéder à cette ressource.',
  'status.saved': 'Enregistré',
  'status.saving': 'Enregistrement...',
  'status.connecting': 'Connexion...',
  'status.connected': 'Connecté',
  'workspace.parentWorkspace': 'Espace de travail parent',
  'workspace.projectCode': 'Code projet',
  'workspace.createNew': 'Créer un nouvel espace de travail',
  'workspace.name': 'Nom de l\'espace de travail',
  'workspace.editWorkspace': 'Modifier l\'espace de travail',
  'workspace.deleteWorkspace': 'Supprimer l\'espace de travail',
  'workspace.deleteConfirm': 'Êtes-vous sûr de vouloir supprimer cet espace de travail ?',
  'workspace.inviteMembers': 'Inviter des membres',
  'workspace.role': 'Rôle',
  'workspace.admin': 'Administrateur',
  'workspace.member': 'Membre',
  'workspace.viewer': 'Lecteur',
  'workspace.removeMember': 'Supprimer le membre',
  'workspace.pendingInvites': 'Invitations en attente',
  'workspace.noMembers': 'Aucun membre pour l\'instant',
  'workspace.workspaceId': "ID de l'espace de travail",
  'workspace.billingPlan': 'Plan',
  'workspace.billingPaymentInfo': 'Informations de paiement',
  'workspace.billingHistory': 'Historique de facturation',
  'invite.emailPlaceholder': 'Entrez l\'adresse e-mail',
  'invite.sendInvite': 'Envoyer l\'invitation',
  'invite.inviteSent': 'Invitation envoyée',
  'invite.inviteUsers': 'Inviter des utilisateurs',
  'invite.revokeInvite': 'Révoquer l\'invitation',
  'toast.linkCopied': 'Lien copié.',
  'toast.changesSaved': 'Modifications enregistrées.',
  'toast.evaluationArchived': 'Évaluation archivée.',
  'toast.evaluationUnarchived': 'Évaluation désarchivée.',
  'toast.evaluationDeleted': 'Évaluation supprimée.',
  'toast.evaluationRenamed': 'Évaluation renommée.',
  'toast.batchModeActive': 'Le mode par lots est actif. Les modifications seront appliquées lorsque vous quitterez le mode par lots.',
  'toast.switchedToEach': 'Passé en mode individuel.',
  'toast.errorOccurred': 'Une erreur est survenue.',
  'toast.archivingEvaluation': 'Archivage de l\'évaluation.',
  'toast.unarchivingEvaluation': 'Désarchivage de l\'évaluation.',
  'toast.deletingEvaluation': 'Suppression de l\'évaluation.',
  'toast.creatingVersion': 'Création d\'une nouvelle version.',
  'toast.versionCreated': 'Nouvelle version créée.',
  'confirm.deleteEvalTitle': 'Êtes-vous sûr de vouloir supprimer cette évaluation ?',
  'confirm.deleteEvalDescription': 'Voulez-vous supprimer complètement cette évaluation ? Toutes les données associées seront supprimées et ne pourront pas être restaurées.',
  'confirm.recoverEvalTitle': 'Récupérer l\'évaluation depuis la base de données',
  'confirm.recoverEvalDescription': 'ATTENTION : Cela rechargera l\'évaluation depuis la base de données. Les modifications non enregistrées pourraient être perdues.',
  'changelog.tooltip': 'Journal des modifications',
  'changelog.batchWarning': 'Les modifications ne seront pas enregistrées dans le journal des modifications tant que vous êtes en',
  'changelog.batchMode': 'Mode par lots',

  'editor.pasteLink': 'Coller le lien',
  'editor.textToDisplay': 'Texte à afficher',
  'editor.visitLink': 'Visiter le lien',
  'editor.editLink': 'Modifier le lien',
  'editor.removeLink': 'Supprimer le lien',
  'editor.link': 'Lien',
  'editor.comment': 'Commentaire',
  'editor.bold': 'Gras',
  'editor.italic': 'Italique',
  'editor.underline': 'Souligné',
  'editor.editComment': 'Modifier le commentaire',
  'editor.deleteComment': 'Supprimer le commentaire',

  // Account / Profile
  'account.emailAddress': 'Adresse e-mail',
  'account.currentEmail': 'E-mail actuel',
  'account.emailVerify': 'Nous vous enverrons un e-mail pour vérifier le changement.',
  'account.confirmNewPassword': 'Confirmer le nouveau mot de passe',
  'account.savePassword': 'Enregistrer le mot de passe',
  'account.updatingProfile': 'Mise à jour du profil.',
  'account.profileUpdated': 'Profil mis à jour avec succès.',
  'account.profileUpdateError': 'Une erreur est survenue lors de la mise à jour de votre profil. Veuillez réessayer ou contacter le support.',
  'account.updatingPassword': 'Mise à jour du mot de passe.',
}

const de: TranslationKeys = {
  'nav.dashboard': 'Übersicht',
  'nav.evaluations': 'Bewertungen',
  'nav.account': 'Konto',
  'nav.settings': 'Einstellungen',
  'nav.logout': 'Abmelden',
  'nav.workspace': 'Arbeitsbereich',
  'nav.manageAccount': 'Konto verwalten',
  'nav.manageWorkspace': 'Arbeitsbereich verwalten',
  'nav.playWalkthrough': 'Tutorial ansehen',
  'nav.documentation': 'Dokumentation',
  'nav.support': 'Support',
  'nav.talkToExpert': 'Mit einem Experten sprechen',

  'account.title': 'Mein Konto',
  'account.myProfile': 'Mein Profil',
  'account.preferences': 'Einstellungen',
  'account.security': 'Kontosicherheit',
  'account.language': 'Sprache',
  'account.email': 'E-Mail',
  'account.password': 'Passwort',
  'account.loginConnections': 'Anmeldeverbindungen',
  'account.firstName': 'Vorname',
  'account.lastName': 'Nachname',
  'account.changePassword': 'Passwort ändern',
  'account.currentPassword': 'Aktuelles Passwort',
  'account.newPassword': 'Neues Passwort',
  'account.confirmPassword': 'Passwort bestätigen',

  'action.save': 'Speichern',
  'action.cancel': 'Abbrechen',
  'action.delete': 'Löschen',
  'action.edit': 'Bearbeiten',
  'action.create': 'Erstellen',
  'action.close': 'Schließen',
  'action.confirm': 'Bestätigen',
  'action.search': 'Suchen',
  'action.filter': 'Filtern',
  'action.export': 'Exportieren',
  'action.import': 'Importieren',
  'action.refresh': 'Aktualisieren',
  'action.back': 'Zurück',
  'action.next': 'Weiter',
  'action.previous': 'Zurück',
  'action.submit': 'Absenden',
  'action.reset': 'Zurücksetzen',
  'action.update': 'Aktualisieren',
  'action.add': 'Hinzufügen',
  'action.remove': 'Entfernen',
  'action.copy': 'Kopieren',
  'action.download': 'Herunterladen',
  'action.share': 'Teilen',
  'action.invite': 'Einladen',
  'action.retry': 'Erneut versuchen',
  'action.reload': 'Neu laden',
  'action.saving': 'Speichern...',
  'action.ok': 'OK',

  'dashboard.title': 'Übersicht',
  'dashboard.recentEvaluations': 'Aktuelle Bewertungen',
  'dashboard.noEvaluations': 'Noch keine Bewertungen',
  'dashboard.createEvaluation': 'Bewertung erstellen',
  'dashboard.lastUpdated': 'Zuletzt aktualisiert',
  'dashboard.status': 'Status',
  'dashboard.viewAll': 'Alle anzeigen',
  'dashboard.gettingStarted': 'Erste Schritte',
  'dashboard.myEvaluations': 'Meine Bewertungen',
  'dashboard.shared': 'Geteilt',
  'dashboard.archived': 'Archiviert',
  'dashboard.backToAccount': 'Zurück zum Konto',
  'dashboard.backToDashboard': 'Zurück zur Übersicht',
  'dashboard.emptyOwned': 'Alle Bewertungen, die Sie erstellen oder erhalten, werden hier angezeigt.',
  'dashboard.emptyShared': 'Alle Bewertungen, bei denen Sie Mitarbeiter sind, werden hier angezeigt.',
  'dashboard.emptyArchived': 'Archivierte Bewertungen werden hier angezeigt.',
  'dashboard.createFirst': 'Meine erste Bewertung erstellen',
  'dashboard.watchWalkthrough': 'Tutorial ansehen',
  'dashboard.watchVideo': 'Sehen Sie unser Einführungsvideo',
  'dashboard.createFirstEval': 'Erstellen Sie Ihre erste Bewertung',
  'dashboard.scheduleCall': 'Termin vereinbaren',
  'dashboard.bookExpert': 'Vereinbaren Sie einen Termin mit einem unserer Experten',

  'eval.title': 'Bewertung',
  'eval.newEvaluation': 'Neue Bewertung',
  'eval.products': 'Produkte',
  'eval.scores': 'Ergebnisse',
  'eval.introduction': 'Einleitung',
  'eval.conclusion': 'Fazit',
  'eval.changeLogs': 'Änderungsprotokoll',
  'eval.template': 'Vorlage',
  'eval.status.draft': 'Entwurf',
  'eval.status.active': 'Aktiv',
  'eval.status.archived': 'Archiviert',
  'eval.status.completed': 'Abgeschlossen',
  'eval.recover': 'Wiederherstellen',
  'eval.updating': 'Aktualisierung...',
  'eval.loading': 'Laden...',

  'label.name': 'Name',
  'label.description': 'Beschreibung',
  'label.date': 'Datum',
  'label.type': 'Typ',
  'label.category': 'Kategorie',
  'label.vendor': 'Anbieter',
  'label.product': 'Produkt',
  'label.score': 'Ergebnis',
  'label.rating': 'Bewertung',
  'label.notes': 'Notizen',
  'label.comments': 'Kommentare',
  'label.createdAt': 'Erstellt',
  'label.updatedAt': 'Aktualisiert',
  'label.createdBy': 'Erstellt von',
  'label.all': 'Alle',
  'label.none': 'Keine',
  'label.loading': 'Laden...',
  'label.noResults': 'Keine Ergebnisse gefunden',
  'label.error': 'Ein Fehler ist aufgetreten',
  'label.success': 'Erfolg',
  'label.warning': 'Warnung',
  'label.lastModified': 'Zuletzt geändert',
  'label.owner': 'Eigentümer',
  'label.collaborators': 'Mitarbeiter',

  'evalCreate.categoryHeading': 'Sparen Sie über 20 Stunden Recherche in 5 Minuten',
  'evalCreate.categorySubtext': 'Bitte wählen Sie eine Produktkategorie aus der Liste unten.',
  'evalCreate.categorySearch': 'Suchen oder auf eine Option klicken',
  'evalCreate.categoryNoProducts': 'Keine Produkte für diese Kategorie gefunden.',
  'evalCreate.categoryStale': 'Diese Kategorie ist über 90 Tage alt.',
  'evalCreate.categoryRequestRefresh': 'Aktualisierung anfordern',
  'evalCreate.categoryProductsIncluded': 'Folgende Produkte sind enthalten:',
  'evalCreate.categoryNotFound': 'Nicht gefunden, was Sie suchen?',
  'evalCreate.useCaseHeading': 'Welche Anwendungsfälle sind für Sie relevant?',
  'evalCreate.useCaseSubtext': 'Wir haben einige grundlegende Anwendungsfälle ausgewählt, aber Sie können so viele hinzufügen, wie Sie möchten.',
  'evalCreate.useCaseSearch': 'Anwendungsfälle suchen',
  'evalCreate.useCaseSelectAll': 'Alle auswählen',
  'evalCreate.useCaseDeselectAll': 'Alle abwählen',
  'evalCreate.useCaseSelected': 'Anwendungsfälle ausgewählt',
  'evalCreate.useCaseLooksGood': 'Sieht gut aus',
  'evalCreate.useCaseNone': 'Kein Anwendungsfall gefunden.',
  'evalCreate.useCaseNotFound': 'Keine Anwendungsfälle für diese Kategorie gefunden.',
  'evalCreate.requestNewCategory': 'Neue Kategorie anfordern',
  'evalCreate.categoryPanelHeading': 'Finden Sie nicht, was Sie brauchen?',
  'evalCreate.categoryPanelSubtext': 'Wir können innerhalb von 2 Werktagen eine neue Kategorie für Sie recherchieren.',

  'workspace.manage': 'Arbeitsbereich verwalten',
  'workspace.general': 'Allgemein',
  'workspace.members': 'Mitglieder',
  'workspace.billing': 'Abrechnung',

  'rating.Great': 'Ausgezeichnet',
  'rating.Good': 'Gut',
  'rating.OK': 'Akzeptabel',
  'rating.Poor': 'Schlecht',
  'rating.Excellent': 'Ausgezeichnet',
  'rating.Acceptable': 'Akzeptabel',
  'rating.NA': 'K.A.',
  'priority.Critical': 'Kritisch',
  'priority.Important': 'Wichtig',
  'priority.NiceToHave': 'Wünschenswert',
  'priority.DontCare': 'Unwichtig',

  'eval.selectAnOption': 'Option auswählen',

  'section.Features': 'Funktionen',
  'section.Dimensions': 'Dimensionen',
  'section.Requirements': 'Anforderungen',
  'section.Products': 'Produkte',
  'section.ImpactByDimension': 'Auswirkung nach Dimension',
  'section.ImpactByRequirement': 'Auswirkung nach Anforderung',

  'eval.createNew': 'Neu erstellen',
  'eval.addDimension': 'Dimension hinzufügen',
  'eval.addRequirement': 'Anforderung hinzufügen',
  'eval.addFeature': 'Funktion hinzufügen',
  'eval.rank': 'Rang',
  'eval.rankExplanation': 'Rangerklärung',
  'eval.criticalFeatures': 'Kritische Funktionen',
  'eval.importantFeatures': 'Wichtige Funktionen',
  'eval.meets': 'Erfüllt',
  'eval.doesNotMeet': 'Erfüllt nicht',
  'eval.ratingLabel': 'Bewertung',
  'eval.reasoning': 'Begründung',
  'eval.reasoningPlaceholder': 'Beschreiben Sie den Grund für diese Änderung',
  'eval.reasoningWarning': 'Die Bewertung hat sich geändert und die vorherige Begründung ist möglicherweise nicht mehr relevant.',

  'eval.tabReport': 'Bericht',
  'eval.tabTables': 'Tabellen',
  'eval.tabQuestions': 'Fragen',
  'eval.share': 'Teilen',
  'eval.rename': 'Umbenennen',
  'eval.copyLink': 'Link kopieren',
  'eval.downloadReport': 'Bericht herunterladen',
  'eval.downloadTables': 'Tabellen herunterladen',
  'eval.downloadScoreDetail': 'Bewertungsdetail herunterladen',
  'eval.archive': 'Archivieren',
  'eval.unarchive': 'Dearchivieren',
  'eval.versions': 'Versionen',
  'eval.newVersion': 'Neue Version',

  'chart.meetsAll': 'Erfüllt alle kritischen und wichtigen Funktionen',
  'chart.meetsCriticalNotImportant': 'Erfüllt alle kritischen, aber nicht alle wichtigen',
  'chart.meetsImportantNotCritical': 'Erfüllt nicht alle kritischen, aber alle wichtigen',
  'chart.meetsNone': 'Erfüllt nicht alle kritischen und wichtigen Funktionen',
  'chart.noProducts': 'Es gibt keine Produkte, die hier angezeigt werden können.',

  'eval.batchMode': 'Stapelmodus',
  'eval.batchChanges': 'Änderungen stapeln',
  'eval.batchModeActive': 'Stapelmodus aktiv',
  'eval.batchModeExitFirst': 'Bitte beenden Sie zuerst den Stapelmodus.',

  'eval.disconnected': 'Getrennt',
  'eval.repairing': 'Reparieren...',
  'eval.restoring': 'Wiederherstellen...',
  'eval.syncing': 'Synchronisieren...',
  'eval.updatingCount': 'aktualisieren...',
  'eval.updatesFailed': 'fehlgeschlagen',
  'eval.updated': 'Aktualisiert',

  'eval.outOfFreeChanges': 'Ihre kostenlosen Änderungen sind aufgebraucht. ',
  'eval.upgradeToUnlock': 'Jetzt upgraden für vollen Zugriff.',

  'eval.addAnother': 'Weiteres hinzufügen',

  'comparison.dimension': 'Dimension',
  'comparison.requirement': 'Anforderung',
  'comparison.feature': 'Funktion',
  'comparison.weight': 'Gewicht(%)',
  'comparison.priority': 'Priorität',
  'comparison.impact': 'Auswirkung',
  'comparison.selectType': 'Typ auswählen',
  'comparison.selectProduct': 'Produkt auswählen',
  'comparison.noRows': 'Keine Zeilen gefunden. Bitte aktualisieren Sie Ihre Auswahl.',
  'comparison.comparison': 'Vergleich',

  'sidebar.contents': 'Inhalt',
  'sidebar.tables': 'Tabellen',
  'sidebar.questions': 'Fragen',
  'sidebar.changeLog': 'Änderungsprotokoll',

  'batch.enterTitle': 'Stapelmodus aktivieren?',
  'batch.enterDescription': 'Im Stapelmodus werden Änderungen, die Sie und andere vornehmen, die zugrunde liegenden Berichte nicht aktualisieren, bis Sie den Stapelmodus verlassen. Dies ist nützlich, wenn Sie viele Änderungen auf einmal vornehmen. Alle Benutzer werden diese Modusänderung sehen.',
  'batch.enterConfirm': 'Stapel',
  'batch.cancelTitle': 'Stapelmodus abbrechen?',
  'batch.cancelDescription': 'Dies verwirft alle während des Stapelmodus vorgenommenen Änderungen und kehrt zum Snapshot zurück, der beim Eintritt in den Stapelmodus erstellt wurde. Diese Aktion kann nicht rückgängig gemacht werden. Dies betrifft alle Benutzer.',
  'batch.cancelConfirm': 'Stapel abbrechen (Änderungen verwerfen)',

  'filter.filteringBy': 'Filtern nach',
  'filter.selectAll': 'Alle auswählen',
  'filter.deselectAll': 'Alle abwählen',
  'filter.noContent': 'Kein Inhalt verfügbar. Versuchen Sie, neue Daten hinzuzufügen oder Ihre Filter zu aktualisieren.',

  'error.somethingWentWrong': 'Hoppla! Etwas ist schiefgelaufen.',
  'error.tryAgainOrRefresh': 'Versuchen Sie es erneut oder aktualisieren Sie die Seite. Wenn das Problem weiterhin besteht, kontaktieren Sie uns.',
  'error.contactSupport': 'Bitte kontaktieren Sie den Support.',
  'error.pageNotFound': 'Seite nicht gefunden',
  'error.returnToDashboard': 'Zurück zur Übersicht',

  'status.viewOnly': 'Nur Ansicht',
  'status.linkCopied': 'Link kopiert.',
  'status.noneAvailable': 'Nicht verfügbar',
  'error.accessDenied': 'Zugriff verweigert',
  'error.accessDeniedDescription': 'Sie haben keine Berechtigung, auf diese Ressource zuzugreifen.',
  'status.saved': 'Gespeichert',
  'status.saving': 'Speichern...',
  'status.connecting': 'Verbinden...',
  'status.connected': 'Verbunden',
  'workspace.parentWorkspace': 'Übergeordneter Arbeitsbereich',
  'workspace.projectCode': 'Projektcode',
  'workspace.createNew': 'Neuen Arbeitsbereich erstellen',
  'workspace.name': 'Name des Arbeitsbereichs',
  'workspace.editWorkspace': 'Arbeitsbereich bearbeiten',
  'workspace.deleteWorkspace': 'Arbeitsbereich löschen',
  'workspace.deleteConfirm': 'Sind Sie sicher, dass Sie diesen Arbeitsbereich löschen möchten?',
  'workspace.inviteMembers': 'Mitglieder einladen',
  'workspace.role': 'Rolle',
  'workspace.admin': 'Administrator',
  'workspace.member': 'Mitglied',
  'workspace.viewer': 'Betrachter',
  'workspace.removeMember': 'Mitglied entfernen',
  'workspace.pendingInvites': 'Ausstehende Einladungen',
  'workspace.noMembers': 'Noch keine Mitglieder',
  'workspace.workspaceId': 'Arbeitsbereich-ID',
  'workspace.billingPlan': 'Tarif',
  'workspace.billingPaymentInfo': 'Zahlungsinformationen',
  'workspace.billingHistory': 'Rechnungsverlauf',
  'invite.emailPlaceholder': 'E-Mail-Adresse eingeben',
  'invite.sendInvite': 'Einladung senden',
  'invite.inviteSent': 'Einladung gesendet',
  'invite.inviteUsers': 'Benutzer einladen',
  'invite.revokeInvite': 'Einladung widerrufen',
  'toast.linkCopied': 'Link kopiert.',
  'toast.changesSaved': 'Änderungen gespeichert.',
  'toast.evaluationArchived': 'Bewertung archiviert.',
  'toast.evaluationUnarchived': 'Bewertung dearchiviert.',
  'toast.evaluationDeleted': 'Bewertung gelöscht.',
  'toast.evaluationRenamed': 'Bewertung umbenannt.',
  'toast.batchModeActive': 'Stapelmodus ist aktiv. Änderungen werden beim Verlassen des Stapelmodus angewendet.',
  'toast.switchedToEach': 'Auf Einzelmodus umgeschaltet.',
  'toast.errorOccurred': 'Ein Fehler ist aufgetreten.',
  'toast.archivingEvaluation': 'Bewertung wird archiviert.',
  'toast.unarchivingEvaluation': 'Bewertung wird wiederhergestellt.',
  'toast.deletingEvaluation': 'Bewertung wird gelöscht.',
  'toast.creatingVersion': 'Neue Version wird erstellt.',
  'toast.versionCreated': 'Neue Version erstellt.',
  'confirm.deleteEvalTitle': 'Sind Sie sicher, dass Sie diese Bewertung löschen möchten?',
  'confirm.deleteEvalDescription': 'Möchten Sie diese Bewertung vollständig löschen? Alle zugehörigen Daten werden entfernt und können nicht wiederhergestellt werden.',
  'confirm.recoverEvalTitle': 'Bewertung aus der Datenbank wiederherstellen',
  'confirm.recoverEvalDescription': 'ACHTUNG: Dies wird die Bewertung aus der Datenbank neu laden. Aktuelle nicht gespeicherte Änderungen könnten verloren gehen.',
  'changelog.tooltip': 'Änderungsprotokoll',
  'changelog.batchWarning': 'Änderungen werden nicht im Änderungsprotokoll erfasst, solange Sie sich im',
  'changelog.batchMode': 'Stapelmodus',

  'editor.pasteLink': 'Link einfügen',
  'editor.textToDisplay': 'Anzuzeigender Text',
  'editor.visitLink': 'Link besuchen',
  'editor.editLink': 'Link bearbeiten',
  'editor.removeLink': 'Link entfernen',
  'editor.link': 'Link',
  'editor.comment': 'Kommentar',
  'editor.bold': 'Fett',
  'editor.italic': 'Kursiv',
  'editor.underline': 'Unterstrichen',
  'editor.editComment': 'Kommentar bearbeiten',
  'editor.deleteComment': 'Kommentar löschen',

  // Account / Profile
  'account.emailAddress': 'E-Mail-Adresse',
  'account.currentEmail': 'Aktuelle E-Mail',
  'account.emailVerify': 'Wir senden Ihnen eine E-Mail zur Bestätigung der Änderung.',
  'account.confirmNewPassword': 'Neues Passwort bestätigen',
  'account.savePassword': 'Passwort speichern',
  'account.updatingProfile': 'Profil wird aktualisiert.',
  'account.profileUpdated': 'Profil erfolgreich aktualisiert.',
  'account.profileUpdateError': 'Beim Aktualisieren Ihres Profils ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.',
  'account.updatingPassword': 'Passwort wird aktualisiert.',
}

const pl: TranslationKeys = {
  'nav.dashboard': 'Panel',
  'nav.evaluations': 'Oceny',
  'nav.account': 'Konto',
  'nav.settings': 'Ustawienia',
  'nav.logout': 'Wyloguj',
  'nav.workspace': 'Przestrzeń robocza',
  'nav.manageAccount': 'Zarządzaj kontem',
  'nav.manageWorkspace': 'Zarządzaj przestrzenią roboczą',
  'nav.playWalkthrough': 'Obejrzyj samouczek',
  'nav.documentation': 'Dokumentacja',
  'nav.support': 'Wsparcie',
  'nav.talkToExpert': 'Porozmawiaj z ekspertem',

  'account.title': 'Moje konto',
  'account.myProfile': 'Mój profil',
  'account.preferences': 'Preferencje',
  'account.security': 'Bezpieczeństwo konta',
  'account.language': 'Język',
  'account.email': 'E-mail',
  'account.password': 'Hasło',
  'account.loginConnections': 'Połączenia logowania',
  'account.firstName': 'Imię',
  'account.lastName': 'Nazwisko',
  'account.changePassword': 'Zmień hasło',
  'account.currentPassword': 'Aktualne hasło',
  'account.newPassword': 'Nowe hasło',
  'account.confirmPassword': 'Potwierdź hasło',

  'action.save': 'Zapisz',
  'action.cancel': 'Anuluj',
  'action.delete': 'Usuń',
  'action.edit': 'Edytuj',
  'action.create': 'Utwórz',
  'action.close': 'Zamknij',
  'action.confirm': 'Potwierdź',
  'action.search': 'Szukaj',
  'action.filter': 'Filtruj',
  'action.export': 'Eksportuj',
  'action.import': 'Importuj',
  'action.refresh': 'Odśwież',
  'action.back': 'Wstecz',
  'action.next': 'Dalej',
  'action.previous': 'Poprzedni',
  'action.submit': 'Wyślij',
  'action.reset': 'Resetuj',
  'action.update': 'Aktualizuj',
  'action.add': 'Dodaj',
  'action.remove': 'Usuń',
  'action.copy': 'Kopiuj',
  'action.download': 'Pobierz',
  'action.share': 'Udostępnij',
  'action.invite': 'Zaproś',
  'action.retry': 'Spróbuj ponownie',
  'action.reload': 'Przeładuj',
  'action.saving': 'Zapisywanie...',
  'action.ok': 'OK',

  'dashboard.title': 'Panel',
  'dashboard.recentEvaluations': 'Ostatnie oceny',
  'dashboard.noEvaluations': 'Brak ocen',
  'dashboard.createEvaluation': 'Utwórz ocenę',
  'dashboard.lastUpdated': 'Ostatnia aktualizacja',
  'dashboard.status': 'Status',
  'dashboard.viewAll': 'Zobacz wszystkie',
  'dashboard.gettingStarted': 'Pierwsze kroki',
  'dashboard.myEvaluations': 'Moje oceny',
  'dashboard.shared': 'Udostępnione',
  'dashboard.archived': 'Zarchiwizowane',
  'dashboard.backToAccount': 'Powrót do konta',
  'dashboard.backToDashboard': 'Powrót do panelu',
  'dashboard.emptyOwned': 'Utworzone lub otrzymane oceny pojawią się tutaj.',
  'dashboard.emptyShared': 'Oceny, w których jesteś współpracownikiem, pojawią się tutaj.',
  'dashboard.emptyArchived': 'Zarchiwizowane oceny pojawią się tutaj.',
  'dashboard.createFirst': 'Utwórz moją pierwszą ocenę',
  'dashboard.watchWalkthrough': 'Obejrzyj samouczek',
  'dashboard.watchVideo': 'Obejrzyj nasz film wprowadzający',
  'dashboard.createFirstEval': 'Utwórz swoją pierwszą ocenę',
  'dashboard.scheduleCall': 'Umów rozmowę',
  'dashboard.bookExpert': 'Umów się z jednym z naszych ekspertów',

  'eval.title': 'Ocena',
  'eval.newEvaluation': 'Nowa ocena',
  'eval.products': 'Produkty',
  'eval.scores': 'Wyniki',
  'eval.introduction': 'Wprowadzenie',
  'eval.conclusion': 'Podsumowanie',
  'eval.changeLogs': 'Dziennik zmian',
  'eval.template': 'Szablon',
  'eval.status.draft': 'Szkic',
  'eval.status.active': 'Aktywna',
  'eval.status.archived': 'Zarchiwizowana',
  'eval.status.completed': 'Ukończona',
  'eval.recover': 'Przywróć',
  'eval.updating': 'Aktualizowanie...',
  'eval.loading': 'Ładowanie...',

  'label.name': 'Nazwa',
  'label.description': 'Opis',
  'label.date': 'Data',
  'label.type': 'Typ',
  'label.category': 'Kategoria',
  'label.vendor': 'Dostawca',
  'label.product': 'Produkt',
  'label.score': 'Wynik',
  'label.rating': 'Ocena',
  'label.notes': 'Notatki',
  'label.comments': 'Komentarze',
  'label.createdAt': 'Utworzono',
  'label.updatedAt': 'Zaktualizowano',
  'label.createdBy': 'Utworzone przez',
  'label.all': 'Wszystkie',
  'label.none': 'Brak',
  'label.loading': 'Ładowanie...',
  'label.noResults': 'Nie znaleziono wyników',
  'label.error': 'Wystąpił błąd',
  'label.success': 'Sukces',
  'label.warning': 'Ostrzeżenie',
  'label.lastModified': 'Ostatnia modyfikacja',
  'label.owner': 'Właściciel',
  'label.collaborators': 'Współpracownicy',

  'evalCreate.categoryHeading': 'Zaoszczędź ponad 20 godzin badań w 5 minut',
  'evalCreate.categorySubtext': 'Wybierz kategorię produktu z poniższej listy.',
  'evalCreate.categorySearch': 'Szukaj lub kliknij opcję',
  'evalCreate.categoryNoProducts': 'Nie znaleziono produktów dla tej kategorii.',
  'evalCreate.categoryStale': 'Ta kategoria ma ponad 90 dni.',
  'evalCreate.categoryRequestRefresh': 'Poproś o odświeżenie',
  'evalCreate.categoryProductsIncluded': 'Następujące produkty są uwzględnione:',
  'evalCreate.categoryNotFound': 'Nie widzisz tego, czego potrzebujesz?',
  'evalCreate.useCaseHeading': 'Które przypadki użycia są dla Ciebie istotne?',
  'evalCreate.useCaseSubtext': 'Wybraliśmy kilka podstawowych przypadków użycia na start, ale możesz dodać tyle, ile chcesz.',
  'evalCreate.useCaseSearch': 'Szukaj przypadków użycia',
  'evalCreate.useCaseSelectAll': 'Zaznacz wszystkie',
  'evalCreate.useCaseDeselectAll': 'Odznacz wszystkie',
  'evalCreate.useCaseSelected': 'Wybrane przypadki użycia',
  'evalCreate.useCaseLooksGood': 'Wygląda dobrze',
  'evalCreate.useCaseNone': 'Nie znaleziono przypadku użycia.',
  'evalCreate.useCaseNotFound': 'Nie znaleziono przypadków użycia dla tej kategorii.',
  'evalCreate.requestNewCategory': 'Poproś o nową kategorię',
  'evalCreate.categoryPanelHeading': 'Nie widzisz tego, czego potrzebujesz?',
  'evalCreate.categoryPanelSubtext': 'Możemy zbadać nową kategorię dla Ciebie w ciągu 2 dni roboczych.',

  'workspace.manage': 'Zarządzaj przestrzenią roboczą',
  'workspace.general': 'Ogólne',
  'workspace.members': 'Członkowie',
  'workspace.billing': 'Rozliczenia',

  'rating.Great': 'Świetny',
  'rating.Good': 'Dobry',
  'rating.OK': 'Dostateczny',
  'rating.Poor': 'Słaby',
  'rating.Excellent': 'Doskonały',
  'rating.Acceptable': 'Akceptowalny',
  'rating.NA': 'Nie dot.',
  'priority.Critical': 'Krytyczny',
  'priority.Important': 'Ważny',
  'priority.NiceToHave': 'Pożądany',
  'priority.DontCare': 'Nieistotny',

  'eval.selectAnOption': 'Wybierz opcję',

  'section.Features': 'Funkcje',
  'section.Dimensions': 'Wymiary',
  'section.Requirements': 'Wymagania',
  'section.Products': 'Produkty',
  'section.ImpactByDimension': 'Wpływ wg wymiaru',
  'section.ImpactByRequirement': 'Wpływ wg wymagania',

  'eval.createNew': 'Utwórz nowy',
  'eval.addDimension': 'Dodaj wymiar',
  'eval.addRequirement': 'Dodaj wymaganie',
  'eval.addFeature': 'Dodaj funkcję',
  'eval.rank': 'Pozycja',
  'eval.rankExplanation': 'Wyjaśnienie pozycji',
  'eval.criticalFeatures': 'Krytyczne funkcje',
  'eval.importantFeatures': 'Ważne funkcje',
  'eval.meets': 'Spełnia',
  'eval.doesNotMeet': 'Nie spełnia',
  'eval.ratingLabel': 'Ocena',
  'eval.reasoning': 'Uzasadnienie',
  'eval.reasoningPlaceholder': 'Opisz powód tej zmiany',
  'eval.reasoningWarning': 'Ocena uległa zmianie i poprzednie uzasadnienie może nie być już aktualne.',

  'eval.tabReport': 'Raport',
  'eval.tabTables': 'Tabele',
  'eval.tabQuestions': 'Pytania',
  'eval.share': 'Udostępnij',
  'eval.rename': 'Zmień nazwę',
  'eval.copyLink': 'Kopiuj link',
  'eval.downloadReport': 'Pobierz raport',
  'eval.downloadTables': 'Pobierz tabele',
  'eval.downloadScoreDetail': 'Pobierz szczegóły wyników',
  'eval.archive': 'Archiwizuj',
  'eval.unarchive': 'Przywróć z archiwum',
  'eval.versions': 'Wersje',
  'eval.newVersion': 'Nowa wersja',

  'chart.meetsAll': 'Spełnia wszystkie krytyczne i ważne funkcje',
  'chart.meetsCriticalNotImportant': 'Spełnia wszystkie krytyczne, ale nie wszystkie ważne',
  'chart.meetsImportantNotCritical': 'Nie spełnia wszystkich krytycznych, ale spełnia wszystkie ważne',
  'chart.meetsNone': 'Nie spełnia wszystkich krytycznych i ważnych funkcji',
  'chart.noProducts': 'Brak produktów do wyświetlenia.',

  'eval.batchMode': 'Tryb wsadowy',
  'eval.batchChanges': 'Zmiany wsadowe',
  'eval.batchModeActive': 'Tryb wsadowy aktywny',
  'eval.batchModeExitFirst': 'Najpierw wyjdź z trybu wsadowego.',

  'eval.disconnected': 'Rozłączono',
  'eval.repairing': 'Naprawianie...',
  'eval.restoring': 'Przywracanie...',
  'eval.syncing': 'Synchronizacja...',
  'eval.updatingCount': 'aktualizowanie...',
  'eval.updatesFailed': 'niepowodzenie',
  'eval.updated': 'Zaktualizowano',

  'eval.outOfFreeChanges': 'Wykorzystano darmowe zmiany w ocenie. ',
  'eval.upgradeToUnlock': 'Uaktualnij, aby odblokować pełny dostęp.',

  'eval.addAnother': 'Dodaj kolejny',

  'comparison.dimension': 'Wymiar',
  'comparison.requirement': 'Wymaganie',
  'comparison.feature': 'Funkcja',
  'comparison.weight': 'Waga(%)',
  'comparison.priority': 'Priorytet',
  'comparison.impact': 'Wpływ',
  'comparison.selectType': 'Wybierz typ',
  'comparison.selectProduct': 'Wybierz produkt',
  'comparison.noRows': 'Nie znaleziono wierszy. Spróbuj zaktualizować swoje wybory.',
  'comparison.comparison': 'porównanie',

  'sidebar.contents': 'Spis treści',
  'sidebar.tables': 'Tabele',
  'sidebar.questions': 'Pytania',
  'sidebar.changeLog': 'Dziennik zmian',

  'batch.enterTitle': 'Włączyć tryb wsadowy?',
  'batch.enterDescription': 'W trybie wsadowym zmiany dokonywane przez Ciebie i innych nie zaktualizują raportów bazowych, dopóki nie wyjdziesz z trybu wsadowego. Jest to przydatne przy wprowadzaniu wielu zmian naraz. Wszyscy użytkownicy zobaczą tę zmianę trybu.',
  'batch.enterConfirm': 'Wsadowy',
  'batch.cancelTitle': 'Anulować tryb wsadowy?',
  'batch.cancelDescription': 'Spowoduje to odrzucenie wszystkich zmian dokonanych w trybie wsadowym i powrót do migawki wykonanej przy wejściu w tryb wsadowy. Tej akcji nie można cofnąć. Dotyczy to wszystkich użytkowników.',
  'batch.cancelConfirm': 'Anuluj wsadowy (Odrzuć zmiany)',

  'filter.filteringBy': 'Filtrowanie po',
  'filter.selectAll': 'Zaznacz wszystko',
  'filter.deselectAll': 'Odznacz wszystko',
  'filter.noContent': 'Brak dostępnej zawartości. Spróbuj dodać nowe dane lub zaktualizować filtry.',

  'error.somethingWentWrong': 'Ups! Coś poszło nie tak.',
  'error.tryAgainOrRefresh': 'Spróbuj ponownie lub odśwież stronę. Jeśli problem się powtarza, skontaktuj się z nami.',
  'error.contactSupport': 'Proszę skontaktować się z pomocą techniczną.',
  'error.pageNotFound': 'Nie znaleziono strony',
  'error.returnToDashboard': 'Powrót do panelu',

  'status.viewOnly': 'Tylko odczyt',
  'status.linkCopied': 'Link skopiowany.',
  'status.noneAvailable': 'Brak dostępnych',
  'error.accessDenied': 'Odmowa dostępu',
  'error.accessDeniedDescription': 'Nie masz uprawnień do dostępu do tego zasobu.',
  'status.saved': 'Zapisano',
  'status.saving': 'Zapisywanie...',
  'status.connecting': 'Łączenie...',
  'status.connected': 'Połączono',
  'workspace.parentWorkspace': 'Nadrzędna przestrzeń robocza',
  'workspace.projectCode': 'Kod projektu',
  'workspace.createNew': 'Utwórz nową przestrzeń roboczą',
  'workspace.name': 'Nazwa przestrzeni roboczej',
  'workspace.editWorkspace': 'Edytuj przestrzeń roboczą',
  'workspace.deleteWorkspace': 'Usuń przestrzeń roboczą',
  'workspace.deleteConfirm': 'Czy na pewno chcesz usunąć tę przestrzeń roboczą?',
  'workspace.inviteMembers': 'Zaproś członków',
  'workspace.role': 'Rola',
  'workspace.admin': 'Administrator',
  'workspace.member': 'Członek',
  'workspace.viewer': 'Obserwator',
  'workspace.removeMember': 'Usuń członka',
  'workspace.pendingInvites': 'Oczekujące zaproszenia',
  'workspace.noMembers': 'Brak członków',
  'workspace.workspaceId': 'ID przestrzeni roboczej',
  'workspace.billingPlan': 'Plan',
  'workspace.billingPaymentInfo': 'Informacje o płatnościach',
  'workspace.billingHistory': 'Historia rozliczeń',
  'invite.emailPlaceholder': 'Wprowadź adres e-mail',
  'invite.sendInvite': 'Wyślij zaproszenie',
  'invite.inviteSent': 'Zaproszenie wysłane',
  'invite.inviteUsers': 'Zaproś użytkowników',
  'invite.revokeInvite': 'Cofnij zaproszenie',
  'toast.linkCopied': 'Link skopiowany.',
  'toast.changesSaved': 'Zmiany zapisane.',
  'toast.evaluationArchived': 'Ocena zarchiwizowana.',
  'toast.evaluationUnarchived': 'Ocena przywrócona z archiwum.',
  'toast.evaluationDeleted': 'Ocena usunięta.',
  'toast.evaluationRenamed': 'Ocena przemianowana.',
  'toast.batchModeActive': 'Tryb wsadowy jest aktywny. Zmiany zostaną zastosowane po wyjściu z trybu wsadowego.',
  'toast.switchedToEach': 'Przełączono na tryb indywidualny.',
  'toast.errorOccurred': 'Wystąpił błąd.',
  'toast.archivingEvaluation': 'Archiwizowanie oceny.',
  'toast.unarchivingEvaluation': 'Przywracanie oceny z archiwum.',
  'toast.deletingEvaluation': 'Usuwanie oceny.',
  'toast.creatingVersion': 'Tworzenie nowej wersji.',
  'toast.versionCreated': 'Nowa wersja utworzona.',
  'confirm.deleteEvalTitle': 'Czy na pewno chcesz usunąć tę ocenę?',
  'confirm.deleteEvalDescription': 'Czy chcesz całkowicie usunąć tę ocenę? Wszystkie powiązane dane zostaną usunięte i nie będą mogły być przywrócone.',
  'confirm.recoverEvalTitle': 'Odzyskaj ocenę z bazy danych',
  'confirm.recoverEvalDescription': 'UWAGA: Spowoduje to ponowne pobranie oceny z bazy danych. Bieżące niezapisane zmiany mogą zostać utracone.',
  'changelog.tooltip': 'Dziennik zmian',
  'changelog.batchWarning': 'Zmiany nie będą rejestrowane w dzienniku zmian, gdy jesteś w',
  'changelog.batchMode': 'Trybie wsadowym',

  'editor.pasteLink': 'Wklej link',
  'editor.textToDisplay': 'Tekst do wyświetlenia',
  'editor.visitLink': 'Odwiedź link',
  'editor.editLink': 'Edytuj link',
  'editor.removeLink': 'Usuń link',
  'editor.link': 'Link',
  'editor.comment': 'Komentarz',
  'editor.bold': 'Pogrubienie',
  'editor.italic': 'Kursywa',
  'editor.underline': 'Podkreślenie',
  'editor.editComment': 'Edytuj komentarz',
  'editor.deleteComment': 'Usuń komentarz',

  // Account / Profile
  'account.emailAddress': 'Adres e-mail',
  'account.currentEmail': 'Aktualny e-mail',
  'account.emailVerify': 'Wyślemy Ci e-mail w celu weryfikacji zmiany.',
  'account.confirmNewPassword': 'Potwierdź nowe hasło',
  'account.savePassword': 'Zapisz hasło',
  'account.updatingProfile': 'Aktualizowanie profilu.',
  'account.profileUpdated': 'Profil zaktualizowany pomyślnie.',
  'account.profileUpdateError': 'Coś poszło nie tak podczas aktualizacji profilu. Spróbuj ponownie lub skontaktuj się z pomocą techniczną.',
  'account.updatingPassword': 'Aktualizowanie hasła.',
}

const tr: TranslationKeys = {
  'nav.dashboard': 'Gösterge Paneli',
  'nav.evaluations': 'Değerlendirmeler',
  'nav.account': 'Hesap',
  'nav.settings': 'Ayarlar',
  'nav.logout': 'Çıkış yap',
  'nav.workspace': 'Çalışma Alanı',
  'nav.manageAccount': 'Hesabı Yönet',
  'nav.manageWorkspace': 'Çalışma Alanını Yönet',
  'nav.playWalkthrough': 'Tanıtımı İzle',
  'nav.documentation': 'Dokümantasyon',
  'nav.support': 'Destek',
  'nav.talkToExpert': 'Bir uzmanla konuşun',

  'account.title': 'Hesabım',
  'account.myProfile': 'Profilim',
  'account.preferences': 'Tercihler',
  'account.security': 'Hesap Güvenliği',
  'account.language': 'Dil',
  'account.email': 'E-posta',
  'account.password': 'Şifre',
  'account.loginConnections': 'Giriş Bağlantıları',
  'account.firstName': 'Ad',
  'account.lastName': 'Soyad',
  'account.changePassword': 'Şifre değiştir',
  'account.currentPassword': 'Mevcut şifre',
  'account.newPassword': 'Yeni şifre',
  'account.confirmPassword': 'Şifreyi onayla',

  'action.save': 'Kaydet',
  'action.cancel': 'İptal',
  'action.delete': 'Sil',
  'action.edit': 'Düzenle',
  'action.create': 'Oluştur',
  'action.close': 'Kapat',
  'action.confirm': 'Onayla',
  'action.search': 'Ara',
  'action.filter': 'Filtrele',
  'action.export': 'Dışa Aktar',
  'action.import': 'İçe Aktar',
  'action.refresh': 'Yenile',
  'action.back': 'Geri',
  'action.next': 'İleri',
  'action.previous': 'Önceki',
  'action.submit': 'Gönder',
  'action.reset': 'Sıfırla',
  'action.update': 'Güncelle',
  'action.add': 'Ekle',
  'action.remove': 'Kaldır',
  'action.copy': 'Kopyala',
  'action.download': 'İndir',
  'action.share': 'Paylaş',
  'action.invite': 'Davet et',
  'action.retry': 'Tekrar dene',
  'action.reload': 'Yeniden yükle',
  'action.saving': 'Kaydediliyor...',
  'action.ok': 'Tamam',

  'dashboard.title': 'Gösterge Paneli',
  'dashboard.recentEvaluations': 'Son değerlendirmeler',
  'dashboard.noEvaluations': 'Henüz değerlendirme yok',
  'dashboard.createEvaluation': 'Değerlendirme oluştur',
  'dashboard.lastUpdated': 'Son güncelleme',
  'dashboard.status': 'Durum',
  'dashboard.viewAll': 'Tümünü gör',
  'dashboard.gettingStarted': 'Başlarken',
  'dashboard.myEvaluations': 'Değerlendirmelerim',
  'dashboard.shared': 'Paylaşılan',
  'dashboard.archived': 'Arşivlenen',
  'dashboard.backToAccount': 'Hesaba dön',
  'dashboard.backToDashboard': 'Panele dön',
  'dashboard.emptyOwned': 'Oluşturduğunuz veya aldığınız değerlendirmeler burada görünecek.',
  'dashboard.emptyShared': 'İş birlikçi olduğunuz değerlendirmeler burada görünecek.',
  'dashboard.emptyArchived': 'Arşivlenen değerlendirmeler burada görünecek.',
  'dashboard.createFirst': 'İlk değerlendirmemi oluştur',
  'dashboard.watchWalkthrough': 'Tanıtımı izle',
  'dashboard.watchVideo': 'Başlangıç videomuzu izleyin',
  'dashboard.createFirstEval': 'İlk değerlendirmenizi oluşturun',
  'dashboard.scheduleCall': 'Görüşme planla',
  'dashboard.bookExpert': 'Uzmanlarımızdan biriyle görüşme ayarlayın',

  'eval.title': 'Değerlendirme',
  'eval.newEvaluation': 'Yeni değerlendirme',
  'eval.products': 'Ürünler',
  'eval.scores': 'Puanlar',
  'eval.introduction': 'Giriş',
  'eval.conclusion': 'Sonuç',
  'eval.changeLogs': 'Değişiklik günlüğü',
  'eval.template': 'Şablon',
  'eval.status.draft': 'Taslak',
  'eval.status.active': 'Aktif',
  'eval.status.archived': 'Arşivlenmiş',
  'eval.status.completed': 'Tamamlanmış',
  'eval.recover': 'Kurtar',
  'eval.updating': 'Güncelleniyor...',
  'eval.loading': 'Yükleniyor...',

  'label.name': 'Ad',
  'label.description': 'Açıklama',
  'label.date': 'Tarih',
  'label.type': 'Tür',
  'label.category': 'Kategori',
  'label.vendor': 'Tedarikçi',
  'label.product': 'Ürün',
  'label.score': 'Puan',
  'label.rating': 'Derecelendirme',
  'label.notes': 'Notlar',
  'label.comments': 'Yorumlar',
  'label.createdAt': 'Oluşturulma',
  'label.updatedAt': 'Güncellenme',
  'label.createdBy': 'Oluşturan',
  'label.all': 'Tümü',
  'label.none': 'Hiçbiri',
  'label.loading': 'Yükleniyor...',
  'label.noResults': 'Sonuç bulunamadı',
  'label.error': 'Bir hata oluştu',
  'label.success': 'Başarılı',
  'label.warning': 'Uyarı',
  'label.lastModified': 'Son değişiklik',
  'label.owner': 'Sahip',
  'label.collaborators': 'İş birlikçiler',

  'evalCreate.categoryHeading': '5 dakikada 20 saatten fazla araştırmadan tasarruf edin',
  'evalCreate.categorySubtext': 'Lütfen aşağıdaki listeden bir ürün kategorisi seçin.',
  'evalCreate.categorySearch': 'Arayın veya bir seçeneğe tıklayın',
  'evalCreate.categoryNoProducts': 'Bu kategori için ürün bulunamadı.',
  'evalCreate.categoryStale': 'Bu kategori 90 günden eski.',
  'evalCreate.categoryRequestRefresh': 'Yenileme talep et',
  'evalCreate.categoryProductsIncluded': 'Aşağıdaki ürünler dahildir:',
  'evalCreate.categoryNotFound': 'Aradığınızı bulamıyor musunuz?',
  'evalCreate.useCaseHeading': 'Hangi kullanım senaryoları sizin için geçerli?',
  'evalCreate.useCaseSubtext': 'Başlamanız için bazı temel kullanım senaryoları seçtik, ancak istediğiniz kadar ekleyebilirsiniz.',
  'evalCreate.useCaseSearch': 'Kullanım senaryosu ara',
  'evalCreate.useCaseSelectAll': 'Tümünü seç',
  'evalCreate.useCaseDeselectAll': 'Tümünü kaldır',
  'evalCreate.useCaseSelected': 'Seçili Kullanım Senaryoları',
  'evalCreate.useCaseLooksGood': 'İyi görünüyor',
  'evalCreate.useCaseNone': 'Kullanım senaryosu bulunamadı.',
  'evalCreate.useCaseNotFound': 'Bu kategori için kullanım senaryosu bulunamadı.',
  'evalCreate.requestNewCategory': 'Yeni bir kategori talep et',
  'evalCreate.categoryPanelHeading': 'Aradığınızı bulamıyor musunuz?',
  'evalCreate.categoryPanelSubtext': '2 iş günü içinde sizin için yeni bir kategori araştırabiliriz.',

  'workspace.manage': 'Çalışma alanını yönet',
  'workspace.general': 'Genel',
  'workspace.members': 'Üyeler',
  'workspace.billing': 'Faturalandırma',

  'rating.Great': 'Harika',
  'rating.Good': 'İyi',
  'rating.OK': 'Kabul Edilebilir',
  'rating.Poor': 'Zayıf',
  'rating.Excellent': 'Mükemmel',
  'rating.Acceptable': 'Kabul Edilebilir',
  'rating.NA': 'Uygulanamaz',
  'priority.Critical': 'Kritik',
  'priority.Important': 'Önemli',
  'priority.NiceToHave': 'Olsa İyi Olur',
  'priority.DontCare': 'Önemsiz',

  'eval.selectAnOption': 'Bir seçenek seçin',

  'section.Features': 'Özellikler',
  'section.Dimensions': 'Boyutlar',
  'section.Requirements': 'Gereksinimler',
  'section.Products': 'Ürünler',
  'section.ImpactByDimension': 'Boyuta Göre Etki',
  'section.ImpactByRequirement': 'Gereksinime Göre Etki',

  'eval.createNew': 'Yeni oluştur',
  'eval.addDimension': 'Boyut ekle',
  'eval.addRequirement': 'Gereksinim ekle',
  'eval.addFeature': 'Özellik ekle',
  'eval.rank': 'Sıralama',
  'eval.rankExplanation': 'Sıralama Açıklaması',
  'eval.criticalFeatures': 'Kritik özellikler',
  'eval.importantFeatures': 'Önemli özellikler',
  'eval.meets': 'Karşılıyor',
  'eval.doesNotMeet': 'Karşılamıyor',
  'eval.ratingLabel': 'Derecelendirme',
  'eval.reasoning': 'Gerekçe',
  'eval.reasoningPlaceholder': 'Bu değişikliğin nedenini açıklayın',
  'eval.reasoningWarning': 'Derecelendirme değişti ve önceki gerekçe artık geçerli olmayabilir.',

  'eval.tabReport': 'Rapor',
  'eval.tabTables': 'Tablolar',
  'eval.tabQuestions': 'Sorular',
  'eval.share': 'Paylaş',
  'eval.rename': 'Yeniden adlandır',
  'eval.copyLink': 'Bağlantıyı kopyala',
  'eval.downloadReport': 'Raporu indir',
  'eval.downloadTables': 'Tabloları indir',
  'eval.downloadScoreDetail': 'Puan detayını indir',
  'eval.archive': 'Arşivle',
  'eval.unarchive': 'Arşivden çıkar',
  'eval.versions': 'Sürümler',
  'eval.newVersion': 'Yeni Sürüm',

  'chart.meetsAll': 'Tüm kritik ve önemli özellikleri karşılıyor',
  'chart.meetsCriticalNotImportant': 'Tüm kritikleri karşılıyor, ancak tüm önemlileri karşılamıyor',
  'chart.meetsImportantNotCritical': 'Tüm kritikleri karşılamıyor, ancak tüm önemlileri karşılıyor',
  'chart.meetsNone': 'Tüm kritik ve önemli özellikleri karşılamıyor',
  'chart.noProducts': 'Burada gösterilecek ürün yok.',

  'eval.batchMode': 'Toplu mod',
  'eval.batchChanges': 'Toplu değişiklikler',
  'eval.batchModeActive': 'Toplu mod aktif',
  'eval.batchModeExitFirst': 'Lütfen önce toplu moddan çıkın.',

  'eval.disconnected': 'Bağlantı kesildi',
  'eval.repairing': 'Onarılıyor...',
  'eval.restoring': 'Geri yükleniyor...',
  'eval.syncing': 'Senkronize ediliyor...',
  'eval.updatingCount': 'güncelleniyor...',
  'eval.updatesFailed': 'başarısız',
  'eval.updated': 'Güncellendi',

  'eval.outOfFreeChanges': 'Ücretsiz değişiklik hakkınız tükendi. ',
  'eval.upgradeToUnlock': 'Tam erişim için şimdi yükseltin.',

  'eval.addAnother': 'Bir tane daha ekle',

  'comparison.dimension': 'Boyut',
  'comparison.requirement': 'Gereksinim',
  'comparison.feature': 'Özellik',
  'comparison.weight': 'Ağırlık(%)',
  'comparison.priority': 'Öncelik',
  'comparison.impact': 'Etki',
  'comparison.selectType': 'Bir tür seçin',
  'comparison.selectProduct': 'Bir ürün seçin',
  'comparison.noRows': 'Satır bulunamadı. Seçimlerinizi güncellemeyi deneyin.',
  'comparison.comparison': 'karşılaştırma',

  'sidebar.contents': 'İçindekiler',
  'sidebar.tables': 'Tablolar',
  'sidebar.questions': 'Sorular',
  'sidebar.changeLog': 'Değişiklik günlüğü',

  'batch.enterTitle': 'Toplu moda girilsin mi?',
  'batch.enterDescription': 'Toplu modda, sizin ve diğerlerinin yaptığı değişiklikler toplu moddan çıkana kadar alttaki raporları güncellemeyecektir. Aynı anda birçok değişiklik yaparken kullanışlıdır. Tüm kullanıcılar bu mod değişikliğini görecektir.',
  'batch.enterConfirm': 'Toplu',
  'batch.cancelTitle': 'Toplu mod iptal edilsin mi?',
  'batch.cancelDescription': 'Bu, toplu mod sırasında yapılan tüm değişiklikleri iptal edecek ve toplu moda girdiğinizde alınan anlık görüntüye geri dönecektir. Bu işlem geri alınamaz. Bu tüm kullanıcıları etkileyecektir.',
  'batch.cancelConfirm': 'Toplu iptal (Değişiklikleri sil)',

  'filter.filteringBy': 'Filtreleme',
  'filter.selectAll': 'Tümünü seç',
  'filter.deselectAll': 'Tümünü kaldır',
  'filter.noContent': 'İçerik mevcut değil. Yeni veri eklemeyi veya filtrelerinizi güncellemeyi deneyin.',

  'error.somethingWentWrong': 'Hay aksi! Bir şeyler ters gitti.',
  'error.tryAgainOrRefresh': 'Tekrar deneyin veya sayfayı yenileyin. Sorun devam ederse bizimle iletişime geçin.',
  'error.contactSupport': 'Lütfen destekle iletişime geçin.',
  'error.pageNotFound': 'Sayfa Bulunamadı',
  'error.returnToDashboard': 'Panele dön',

  'status.viewOnly': 'Sadece görüntüleme',
  'status.linkCopied': 'Bağlantı kopyalandı.',
  'status.noneAvailable': 'Mevcut değil',
  'error.accessDenied': 'Erişim reddedildi',
  'error.accessDeniedDescription': 'Bu kaynağa erişim izniniz yok.',
  'status.saved': 'Kaydedildi',
  'status.saving': 'Kaydediliyor...',
  'status.connecting': 'Bağlanıyor...',
  'status.connected': 'Bağlandı',
  'workspace.parentWorkspace': 'Üst Çalışma Alanı',
  'workspace.projectCode': 'Proje Kodu',
  'workspace.createNew': 'Yeni çalışma alanı oluştur',
  'workspace.name': 'Çalışma alanı adı',
  'workspace.editWorkspace': 'Çalışma alanını düzenle',
  'workspace.deleteWorkspace': 'Çalışma alanını sil',
  'workspace.deleteConfirm': 'Bu çalışma alanını silmek istediğinizden emin misiniz?',
  'workspace.inviteMembers': 'Üyeleri davet et',
  'workspace.role': 'Rol',
  'workspace.admin': 'Yönetici',
  'workspace.member': 'Üye',
  'workspace.viewer': 'İzleyici',
  'workspace.removeMember': 'Üyeyi kaldır',
  'workspace.pendingInvites': 'Bekleyen davetler',
  'workspace.noMembers': 'Henüz üye yok',
  'workspace.workspaceId': 'Çalışma Alanı Kimliği',
  'workspace.billingPlan': 'Plan',
  'workspace.billingPaymentInfo': 'Ödeme bilgileri',
  'workspace.billingHistory': 'Fatura geçmişi',
  'invite.emailPlaceholder': 'E-posta adresini girin',
  'invite.sendInvite': 'Davet gönder',
  'invite.inviteSent': 'Davet gönderildi',
  'invite.inviteUsers': 'Kullanıcıları davet et',
  'invite.revokeInvite': 'Daveti iptal et',
  'toast.linkCopied': 'Bağlantı kopyalandı.',
  'toast.changesSaved': 'Değişiklikler kaydedildi.',
  'toast.evaluationArchived': 'Değerlendirme arşivlendi.',
  'toast.evaluationUnarchived': 'Değerlendirme arşivden çıkarıldı.',
  'toast.evaluationDeleted': 'Değerlendirme silindi.',
  'toast.evaluationRenamed': 'Değerlendirme yeniden adlandırıldı.',
  'toast.batchModeActive': 'Toplu mod aktif. Değişiklikler toplu moddan çıktığınızda uygulanacaktır.',
  'toast.switchedToEach': 'Bireysel moda geçildi.',
  'toast.errorOccurred': 'Bir hata oluştu.',
  'toast.archivingEvaluation': 'Değerlendirme arşivleniyor.',
  'toast.unarchivingEvaluation': 'Değerlendirme arşivden çıkarılıyor.',
  'toast.deletingEvaluation': 'Değerlendirme siliniyor.',
  'toast.creatingVersion': 'Yeni sürüm oluşturuluyor.',
  'toast.versionCreated': 'Yeni sürüm oluşturuldu.',
  'confirm.deleteEvalTitle': 'Bu değerlendirmeyi silmek istediğinizden emin misiniz?',
  'confirm.deleteEvalDescription': 'Bu değerlendirmeyi tamamen silmek istiyor musunuz? Tüm ilgili veriler kaldırılacak ve geri yüklenemeyecek.',
  'confirm.recoverEvalTitle': 'Değerlendirmeyi veritabanından kurtar',
  'confirm.recoverEvalDescription': 'DİKKAT: Bu, değerlendirmeyi veritabanından yeniden çekecektir. Mevcut kaydedilmemiş değişiklikler kaybolabilir.',
  'changelog.tooltip': 'Değişiklik günlüğü',
  'changelog.batchWarning': 'Değişiklikler, siz',
  'changelog.batchMode': 'Toplu modda',

  'editor.pasteLink': 'Bağlantı yapıştır',
  'editor.textToDisplay': 'Görüntülenecek metin',
  'editor.visitLink': 'Bağlantıyı ziyaret et',
  'editor.editLink': 'Bağlantıyı düzenle',
  'editor.removeLink': 'Bağlantıyı kaldır',
  'editor.link': 'Bağlantı',
  'editor.comment': 'Yorum',
  'editor.bold': 'Kalın',
  'editor.italic': 'İtalik',
  'editor.underline': 'Altı çizili',
  'editor.editComment': 'Yorumu düzenle',
  'editor.deleteComment': 'Yorumu sil',

  // Account / Profile
  'account.emailAddress': 'E-posta adresi',
  'account.currentEmail': 'Mevcut e-posta',
  'account.emailVerify': 'Değişikliği doğrulamak için size e-posta göndereceğiz.',
  'account.confirmNewPassword': 'Yeni şifreyi onayla',
  'account.savePassword': 'Şifreyi kaydet',
  'account.updatingProfile': 'Profil güncelleniyor.',
  'account.profileUpdated': 'Profil başarıyla güncellendi.',
  'account.profileUpdateError': 'Profiliniz güncellenirken bir hata oluştu. Lütfen tekrar deneyin veya destekle iletişime geçin.',
  'account.updatingPassword': 'Şifre güncelleniyor.',
}

const ar: TranslationKeys = {
  'nav.dashboard': 'لوحة التحكم',
  'nav.evaluations': 'التقييمات',
  'nav.account': 'الحساب',
  'nav.settings': 'الإعدادات',
  'nav.logout': 'تسجيل الخروج',
  'nav.workspace': 'مساحة العمل',
  'nav.manageAccount': 'إدارة الحساب',
  'nav.manageWorkspace': 'إدارة مساحة العمل',
  'nav.playWalkthrough': 'تشغيل الجولة التعريفية',
  'nav.documentation': 'الوثائق',
  'nav.support': 'الدعم',
  'nav.talkToExpert': 'تحدث مع خبير',

  'account.title': 'حسابي',
  'account.myProfile': 'ملفي الشخصي',
  'account.preferences': 'التفضيلات',
  'account.security': 'أمان الحساب',
  'account.language': 'اللغة',
  'account.email': 'البريد الإلكتروني',
  'account.password': 'كلمة المرور',
  'account.loginConnections': 'اتصالات تسجيل الدخول',
  'account.firstName': 'الاسم الأول',
  'account.lastName': 'اسم العائلة',
  'account.changePassword': 'تغيير كلمة المرور',
  'account.currentPassword': 'كلمة المرور الحالية',
  'account.newPassword': 'كلمة المرور الجديدة',
  'account.confirmPassword': 'تأكيد كلمة المرور',

  'action.save': 'حفظ',
  'action.cancel': 'إلغاء',
  'action.delete': 'حذف',
  'action.edit': 'تعديل',
  'action.create': 'إنشاء',
  'action.close': 'إغلاق',
  'action.confirm': 'تأكيد',
  'action.search': 'بحث',
  'action.filter': 'تصفية',
  'action.export': 'تصدير',
  'action.import': 'استيراد',
  'action.refresh': 'تحديث',
  'action.back': 'رجوع',
  'action.next': 'التالي',
  'action.previous': 'السابق',
  'action.submit': 'إرسال',
  'action.reset': 'إعادة تعيين',
  'action.update': 'تحديث',
  'action.add': 'إضافة',
  'action.remove': 'إزالة',
  'action.copy': 'نسخ',
  'action.download': 'تنزيل',
  'action.share': 'مشاركة',
  'action.invite': 'دعوة',
  'action.retry': 'حاول مرة أخرى',
  'action.reload': 'إعادة تحميل',
  'action.saving': 'جارٍ الحفظ...',
  'action.ok': 'موافق',

  'dashboard.title': 'لوحة التحكم',
  'dashboard.recentEvaluations': 'التقييمات الأخيرة',
  'dashboard.noEvaluations': 'لا توجد تقييمات بعد',
  'dashboard.createEvaluation': 'إنشاء تقييم',
  'dashboard.lastUpdated': 'آخر تحديث',
  'dashboard.status': 'الحالة',
  'dashboard.viewAll': 'عرض الكل',
  'dashboard.gettingStarted': 'البدء',
  'dashboard.myEvaluations': 'تقييماتي',
  'dashboard.shared': 'مشتركة',
  'dashboard.archived': 'مؤرشفة',
  'dashboard.backToAccount': 'العودة إلى الحساب',
  'dashboard.backToDashboard': 'العودة إلى لوحة التحكم',
  'dashboard.emptyOwned': 'ستظهر هنا أي تقييمات تنشئها أو تتلقاها.',
  'dashboard.emptyShared': 'ستظهر هنا أي تقييمات تتعاون فيها.',
  'dashboard.emptyArchived': 'ستظهر هنا أي تقييمات مؤرشفة.',
  'dashboard.createFirst': 'إنشاء أول تقييم لي',
  'dashboard.watchWalkthrough': 'مشاهدة الجولة التعريفية',
  'dashboard.watchVideo': 'شاهد فيديو البدء',
  'dashboard.createFirstEval': 'أنشئ أول تقييم لك',
  'dashboard.scheduleCall': 'حجز مكالمة',
  'dashboard.bookExpert': 'احجز وقتاً مع أحد خبرائنا',

  'eval.title': 'التقييم',
  'eval.newEvaluation': 'تقييم جديد',
  'eval.products': 'المنتجات',
  'eval.scores': 'النتائج',
  'eval.introduction': 'المقدمة',
  'eval.conclusion': 'الخلاصة',
  'eval.changeLogs': 'سجل التغييرات',
  'eval.template': 'القالب',
  'eval.status.draft': 'مسودة',
  'eval.status.active': 'نشط',
  'eval.status.archived': 'مؤرشف',
  'eval.status.completed': 'مكتمل',
  'eval.recover': 'استعادة',
  'eval.updating': 'جارٍ التحديث...',
  'eval.loading': 'جارٍ التحميل...',

  'label.name': 'الاسم',
  'label.description': 'الوصف',
  'label.date': 'التاريخ',
  'label.type': 'النوع',
  'label.category': 'الفئة',
  'label.vendor': 'المورد',
  'label.product': 'المنتج',
  'label.score': 'النتيجة',
  'label.rating': 'التقييم',
  'label.notes': 'الملاحظات',
  'label.comments': 'التعليقات',
  'label.createdAt': 'تاريخ الإنشاء',
  'label.updatedAt': 'تاريخ التحديث',
  'label.createdBy': 'أنشأه',
  'label.all': 'الكل',
  'label.none': 'لا شيء',
  'label.loading': 'جارٍ التحميل...',
  'label.noResults': 'لم يتم العثور على نتائج',
  'label.error': 'حدث خطأ',
  'label.success': 'نجاح',
  'label.warning': 'تحذير',
  'label.lastModified': 'آخر تعديل',
  'label.owner': 'المالك',
  'label.collaborators': 'المتعاونون',

  'evalCreate.categoryHeading': 'وفّر أكثر من 20 ساعة من البحث في 5 دقائق',
  'evalCreate.categorySubtext': 'يرجى اختيار فئة منتج من القائمة أدناه.',
  'evalCreate.categorySearch': 'ابحث أو انقر على خيار',
  'evalCreate.categoryNoProducts': 'لم يتم العثور على منتجات لهذه الفئة.',
  'evalCreate.categoryStale': 'هذه الفئة عمرها أكثر من 90 يوماً.',
  'evalCreate.categoryRequestRefresh': 'طلب تحديث',
  'evalCreate.categoryProductsIncluded': 'المنتجات التالية مشمولة:',
  'evalCreate.categoryNotFound': 'لا ترى ما تحتاجه؟',
  'evalCreate.useCaseHeading': 'ما حالات الاستخدام المهمة بالنسبة لك؟',
  'evalCreate.useCaseSubtext': 'اخترنا بعض حالات الاستخدام الأساسية للبدء، ولكن يمكنك إضافة ما تشاء.',
  'evalCreate.useCaseSearch': 'البحث في حالات الاستخدام',
  'evalCreate.useCaseSelectAll': 'تحديد الكل',
  'evalCreate.useCaseDeselectAll': 'إلغاء تحديد الكل',
  'evalCreate.useCaseSelected': 'حالات الاستخدام المحددة',
  'evalCreate.useCaseLooksGood': 'يبدو جيداً',
  'evalCreate.useCaseNone': 'لم يتم العثور على حالة استخدام.',
  'evalCreate.useCaseNotFound': 'لم يتم العثور على حالات استخدام لهذه الفئة.',
  'evalCreate.requestNewCategory': 'طلب فئة جديدة',
  'evalCreate.categoryPanelHeading': 'لا ترى ما تحتاجه؟',
  'evalCreate.categoryPanelSubtext': 'يمكننا البحث عن فئة جديدة لك خلال يومي عمل.',

  'workspace.manage': 'إدارة مساحة العمل',
  'workspace.general': 'عام',
  'workspace.members': 'الأعضاء',
  'workspace.billing': 'الفوترة',

  'rating.Great': 'ممتاز',
  'rating.Good': 'جيد',
  'rating.OK': 'مقبول',
  'rating.Poor': 'ضعيف',
  'rating.Excellent': 'متميز',
  'rating.Acceptable': 'مقبول',
  'rating.NA': 'غ/م',
  'priority.Critical': 'حرج',
  'priority.Important': 'مهم',
  'priority.NiceToHave': 'مستحسن',
  'priority.DontCare': 'غير مهم',

  'eval.selectAnOption': 'اختر خياراً',

  'section.Features': 'الميزات',
  'section.Dimensions': 'الأبعاد',
  'section.Requirements': 'المتطلبات',
  'section.Products': 'المنتجات',
  'section.ImpactByDimension': 'التأثير حسب البعد',
  'section.ImpactByRequirement': 'التأثير حسب المتطلب',

  'eval.createNew': 'إنشاء جديد',
  'eval.addDimension': 'إضافة بعد',
  'eval.addRequirement': 'إضافة متطلب',
  'eval.addFeature': 'إضافة ميزة',
  'eval.rank': 'الترتيب',
  'eval.rankExplanation': 'شرح الترتيب',
  'eval.criticalFeatures': 'الميزات الحرجة',
  'eval.importantFeatures': 'الميزات المهمة',
  'eval.meets': 'يستوفي',
  'eval.doesNotMeet': 'لا يستوفي',
  'eval.ratingLabel': 'التقييم',
  'eval.reasoning': 'التبرير',
  'eval.reasoningPlaceholder': 'صف سبب هذا التغيير',
  'eval.reasoningWarning': 'تم تغيير التقييم وقد لا يكون التبرير السابق ذا صلة بعد الآن.',

  'eval.tabReport': 'التقرير',
  'eval.tabTables': 'الجداول',
  'eval.tabQuestions': 'الأسئلة',
  'eval.share': 'مشاركة',
  'eval.rename': 'إعادة تسمية',
  'eval.copyLink': 'نسخ الرابط',
  'eval.downloadReport': 'تنزيل التقرير',
  'eval.downloadTables': 'تنزيل الجداول',
  'eval.downloadScoreDetail': 'تنزيل تفاصيل النتيجة',
  'eval.archive': 'أرشفة',
  'eval.unarchive': 'إلغاء الأرشفة',
  'eval.versions': 'الإصدارات',
  'eval.newVersion': 'إصدار جديد',

  'chart.meetsAll': 'يستوفي جميع الميزات الحرجة والمهمة',
  'chart.meetsCriticalNotImportant': 'يستوفي جميع الميزات الحرجة، لكن لا يستوفي جميع الميزات المهمة',
  'chart.meetsImportantNotCritical': 'لا يستوفي جميع الميزات الحرجة، لكن يستوفي جميع الميزات المهمة',
  'chart.meetsNone': 'لا يستوفي جميع الميزات الحرجة والمهمة',
  'chart.noProducts': 'لا توجد منتجات لعرضها هنا.',

  'eval.batchMode': 'وضع الدفعات',
  'eval.batchChanges': 'تغييرات الدفعة',
  'eval.batchModeActive': 'وضع الدفعات نشط',
  'eval.batchModeExitFirst': 'يرجى الخروج من وضع الدفعات أولاً.',

  'eval.disconnected': 'غير متصل',
  'eval.repairing': 'جارٍ الإصلاح...',
  'eval.restoring': 'جارٍ الاستعادة...',
  'eval.syncing': 'جارٍ المزامنة...',
  'eval.updatingCount': 'جارٍ التحديث...',
  'eval.updatesFailed': 'فشل',
  'eval.updated': 'تم التحديث',

  'eval.outOfFreeChanges': 'لقد استنفدت التغييرات المجانية على تقييمك. ',
  'eval.upgradeToUnlock': 'قم بالترقية الآن لفتح الوصول الكامل.',

  'eval.addAnother': 'إضافة آخر',

  'comparison.dimension': 'البعد',
  'comparison.requirement': 'المتطلب',
  'comparison.feature': 'الميزة',
  'comparison.weight': 'الوزن(%)',
  'comparison.priority': 'الأولوية',
  'comparison.impact': 'التأثير',
  'comparison.selectType': 'اختر نوعاً',
  'comparison.selectProduct': 'اختر منتجاً',
  'comparison.noRows': 'لم يتم العثور على صفوف. يرجى تحديث اختياراتك.',
  'comparison.comparison': 'مقارنة',

  'sidebar.contents': 'المحتويات',
  'sidebar.tables': 'الجداول',
  'sidebar.questions': 'الأسئلة',
  'sidebar.changeLog': 'سجل التغييرات',

  'batch.enterTitle': 'الدخول في وضع الدفعات؟',
  'batch.enterDescription': 'في وضع الدفعات، لن تقوم التغييرات التي تجريها أنت والآخرون بتحديث التقارير الأساسية حتى تخرج من وضع الدفعات. هذا مفيد عند إجراء العديد من التغييرات دفعة واحدة. سيرى جميع المستخدمين تغيير الوضع هذا.',
  'batch.enterConfirm': 'دفعة',
  'batch.cancelTitle': 'إلغاء وضع الدفعات؟',
  'batch.cancelDescription': 'سيؤدي هذا إلى تجاهل جميع التغييرات التي تمت أثناء وضع الدفعات والعودة إلى اللقطة المأخوذة عند دخول وضع الدفعات. لا يمكن التراجع عن هذا الإجراء. سيؤثر هذا على جميع المستخدمين.',
  'batch.cancelConfirm': 'إلغاء الدفعة (تجاهل التغييرات)',

  'filter.filteringBy': 'التصفية حسب',
  'filter.selectAll': 'تحديد الكل',
  'filter.deselectAll': 'إلغاء تحديد الكل',
  'filter.noContent': 'لا يوجد محتوى متاح. حاول إضافة بيانات جديدة أو تحديث عوامل التصفية.',

  'error.somethingWentWrong': 'أوه! حدث خطأ ما.',
  'error.tryAgainOrRefresh': 'حاول مرة أخرى، أو قم بتحديث الصفحة. إذا استمرت المشكلة، تواصل معنا.',
  'error.contactSupport': 'يرجى التواصل مع الدعم.',
  'error.pageNotFound': 'الصفحة غير موجودة',
  'error.returnToDashboard': 'العودة إلى لوحة التحكم',

  'status.viewOnly': 'للقراءة فقط',
  'status.linkCopied': 'تم نسخ الرابط.',
  'status.noneAvailable': 'غير متوفر',
  'error.accessDenied': 'تم رفض الوصول',
  'error.accessDeniedDescription': 'ليس لديك إذن للوصول إلى هذا المورد.',
  'status.saved': 'تم الحفظ',
  'status.saving': 'جارٍ الحفظ...',
  'status.connecting': 'جارٍ الاتصال...',
  'status.connected': 'متصل',
  'workspace.parentWorkspace': 'مساحة العمل الرئيسية',
  'workspace.projectCode': 'رمز المشروع',
  'workspace.createNew': 'إنشاء مساحة عمل جديدة',
  'workspace.name': 'اسم مساحة العمل',
  'workspace.editWorkspace': 'تعديل مساحة العمل',
  'workspace.deleteWorkspace': 'حذف مساحة العمل',
  'workspace.deleteConfirm': 'هل أنت متأكد من أنك تريد حذف مساحة العمل هذه؟',
  'workspace.inviteMembers': 'دعوة أعضاء',
  'workspace.role': 'الدور',
  'workspace.admin': 'مدير',
  'workspace.member': 'عضو',
  'workspace.viewer': 'مشاهد',
  'workspace.removeMember': 'إزالة العضو',
  'workspace.pendingInvites': 'دعوات معلقة',
  'workspace.noMembers': 'لا يوجد أعضاء بعد',
  'workspace.workspaceId': 'معرّف مساحة العمل',
  'workspace.billingPlan': 'الخطة',
  'workspace.billingPaymentInfo': 'معلومات الدفع',
  'workspace.billingHistory': 'سجل الفواتير',
  'invite.emailPlaceholder': 'أدخل عنوان البريد الإلكتروني',
  'invite.sendInvite': 'إرسال الدعوة',
  'invite.inviteSent': 'تم إرسال الدعوة',
  'invite.inviteUsers': 'دعوة المستخدمين',
  'invite.revokeInvite': 'إلغاء الدعوة',
  'toast.linkCopied': 'تم نسخ الرابط.',
  'toast.changesSaved': 'تم حفظ التغييرات.',
  'toast.evaluationArchived': 'تم أرشفة التقييم.',
  'toast.evaluationUnarchived': 'تم إلغاء أرشفة التقييم.',
  'toast.evaluationDeleted': 'تم حذف التقييم.',
  'toast.evaluationRenamed': 'تمت إعادة تسمية التقييم.',
  'toast.batchModeActive': 'وضع الدفعات نشط. سيتم تطبيق التغييرات عند الخروج من وضع الدفعات.',
  'toast.switchedToEach': 'تم التبديل إلى الوضع الفردي.',
  'toast.errorOccurred': 'حدث خطأ.',
  'toast.archivingEvaluation': 'جاري أرشفة التقييم.',
  'toast.unarchivingEvaluation': 'جاري إلغاء أرشفة التقييم.',
  'toast.deletingEvaluation': 'جاري حذف التقييم.',
  'toast.creatingVersion': 'جاري إنشاء نسخة جديدة.',
  'toast.versionCreated': 'تم إنشاء نسخة جديدة.',
  'confirm.deleteEvalTitle': 'هل أنت متأكد من أنك تريد حذف هذا التقييم؟',
  'confirm.deleteEvalDescription': 'هل تريد حذف هذا التقييم بالكامل؟ سيتم إزالة جميع البيانات المرتبطة ولا يمكن استعادتها.',
  'confirm.recoverEvalTitle': 'استرداد التقييم من قاعدة البيانات',
  'confirm.recoverEvalDescription': 'تحذير: سيتم إعادة سحب التقييم من قاعدة البيانات. قد تفقد التغييرات غير المحفوظة الحالية.',
  'changelog.tooltip': 'سجل التغييرات',
  'changelog.batchWarning': 'لن يتم تسجيل التغييرات في سجل التغييرات أثناء تواجدك في',
  'changelog.batchMode': 'وضع الدُفعات',

  'editor.pasteLink': 'لصق الرابط',
  'editor.textToDisplay': 'النص المراد عرضه',
  'editor.visitLink': 'زيارة الرابط',
  'editor.editLink': 'تعديل الرابط',
  'editor.removeLink': 'إزالة الرابط',
  'editor.link': 'رابط',
  'editor.comment': 'تعليق',
  'editor.bold': 'غامق',
  'editor.italic': 'مائل',
  'editor.underline': 'تحته خط',
  'editor.editComment': 'تعديل التعليق',
  'editor.deleteComment': 'حذف التعليق',

  // Account / Profile
  'account.emailAddress': 'عنوان البريد الإلكتروني',
  'account.currentEmail': 'البريد الإلكتروني الحالي',
  'account.emailVerify': 'سنرسل لك بريدًا إلكترونيًا للتحقق من التغيير.',
  'account.confirmNewPassword': 'تأكيد كلمة المرور الجديدة',
  'account.savePassword': 'حفظ كلمة المرور',
  'account.updatingProfile': 'جارٍ تحديث الملف الشخصي.',
  'account.profileUpdated': 'تم تحديث الملف الشخصي بنجاح.',
  'account.profileUpdateError': 'حدث خطأ أثناء تحديث ملفك الشخصي. يرجى المحاولة مرة أخرى أو التواصل مع الدعم.',
  'account.updatingPassword': 'جارٍ تحديث كلمة المرور.',
}

// British English overlay: only keys whose UK spelling differs from `en` go here;
// everything else inherits `en` through the fallback in getTranslation below.
// (The current UI strings have no US/UK spelling divergence, so this is empty.)
const enGB: Partial<TranslationKeys> = {}

const translations: Partial<Record<SupportedLocale, Partial<TranslationKeys>>> = {
  en,
  'en-GB': enGB,
  es,
  fr,
  de,
  pl,
  tr,
  ar,
}

export function getTranslation(locale: SupportedLocale, key: TranslationKey): string {
  const localeTranslations = translations[locale]
  const value = localeTranslations?.[key]
  if (value !== undefined) {
    return value
  }
  return en[key] || key
}

const MENU_CAPTION_KEYS_RAW: Record<string, TranslationKey> = {
  'Download': 'action.download',
  'Download report': 'eval.downloadReport',
  'Download tables': 'eval.downloadTables',
  'Download score detail': 'eval.downloadScoreDetail',
  'Copy link': 'eval.copyLink',
  'Archive': 'eval.archive',
  'Unarchive': 'eval.unarchive',
  'Delete': 'action.delete',
  'Rename': 'eval.rename',
  'Versions': 'eval.versions',
  'New Version': 'eval.newVersion',
  'Recover': 'eval.recover',
}

const MENU_CAPTION_KEYS_LOWER: Record<string, TranslationKey> = Object.fromEntries(
  Object.entries(MENU_CAPTION_KEYS_RAW).map(([k, v]) => [k.toLowerCase(), v])
)

export function getMenuCaptionKey(caption: string): TranslationKey | undefined {
  return MENU_CAPTION_KEYS_LOWER[caption.toLowerCase()]
}

export const BUTTON_TEXT_KEYS: Record<string, TranslationKey> = {
  'Add tag': 'action.add',
  'Add another': 'eval.addAnother',
  'Add dimension': 'eval.addDimension',
  'Add requirement': 'eval.addRequirement',
  'Add feature': 'eval.addFeature',
}

export const OPTION_LABEL_KEYS: Record<string, TranslationKey> = {
  'Great': 'rating.Great',
  'Good': 'rating.Good',
  'OK': 'rating.OK',
  'Poor': 'rating.Poor',
  'Excellent': 'rating.Excellent',
  'Acceptable': 'rating.Acceptable',
  'NA': 'rating.NA',
  'Critical': 'priority.Critical',
  'Important': 'priority.Important',
  'Nice To Have': 'priority.NiceToHave',
  'Nice to Have': 'priority.NiceToHave',
  "Don't Care": 'priority.DontCare',
  "Dont Care": 'priority.DontCare',
}
